// Kyoto, "Maple leaves on Ninenzaka". A late-November night in Higashiyama, seen from the foot of Yasaka-dori where
// the stone steps of Ninenzaka come in from the left: we stand on the wet paved landing at the junction and look east,
// up the stepped lane between two rows of wooden machiya, to the five-storey Yasaka Pagoda (Hokan-ji) lit gold against
// the deep blue sky and the dark Higashiyama hills, a nearly full moon over the lane.
// What is really there, compressed but in order: the lane climbs east between tea houses and sweet shops (lattice
// fronts, noren curtains, paper lanterns under the eaves, the clay-slatted mushiko-mado of the low upper floors), turns
// at its top under the pagoda, whose lowest storey hides behind the roofs; the hills rise behind. On our left a tea
// house at the corner, its red parasol over a bench; on our right a temple's earthen wall with its five white lines, a
// stone lantern, and a big maple leaning over the coping. The pagoda is drawn a little larger than it would look from
// here (a long lens), nothing is moved out of its real order.
// Back to front: sky, moon, stars, two banks of moonlit cloud -> the hills with the lit maples of the temple grounds ->
// far leaves -> the pagoda (floodlit, its glow breathing, wind bells on the corners, a night heron) -> the lane: its
// houses, steps, lanterns and walkers, the projecting signboard (the city's billboard) -> mid leaves -> the corner tea
// house, the wall, the maples -> the wet landing (mirror, puddles, lantern streaks) and its people -> near leaves.
// render(t, { crowd }) thins the walkers and the tea house guests; crowd 0 draws nobody at all (see CROWD).
//
// The loop's timeline (seconds; every period divides 240):
//   event                                               time                          sound
//   a rickshaw crosses the landing (left to right)      6 - 22, 166 - 182
//   the tea house girl serves the bench                 every 40 s (20, 60 ... 220)
//   a shadow crosses the tea house's upstairs shoji     every 24 s (3, 27, 51 ...)
//   a geiko with a red parasol and a lantern walks in   28 - 84 (up the lane, into the dark)
//   a gust: leaves skitter, noren lift, maples sway     44 - 52, 134 - 142, 210 - 218
//     and the shopkeeper sweeps the lane again          (after each gust)
//   the rickshaw comes back (right to left)             86 - 102
//   a couple in kimono climbs the lane                  92 - 146, a photo of the pagoda at 108 - 117
//   the temple bell                                     116                           bell
//     the night heron lifts off the pagoda, circles      116.5 - 150
//   two maiko come down the lane                        140 - 188
//   the cat walks along the wall and back               150 - 160, 226 - 236
//   the temple bell again                               196                           bell
//     the heron glides back to its roof                 200 - 218
// Longest gap between two starts: 17 s (226 -> 3).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, rect, makeHalo, drawHalo, reflect, streak, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const VP = { x: 240, y: 143 };                        // where the lane's lines meet, under the pagoda
const S_FAR = 0.12;                                   // the lane's depth share where it turns at the top (row 150)
const SL = 88, SR = 88;                             // half width (px at the lane's near end) of its left and right fronts
const gY = s => VP.y + 62 * s;                        // ground row at depth share s (1: the lane's foot, row 205)
const hY = (s, h) => VP.y + s * (62 - h);             // row of a height h (px at the near end) above the ground at s
const LAND = 208;                                     // the landing: the paved junction in front of us
const PAG = { cx: 240, tip: 17 };                     // the pagoda's axis and the top of its spire
// eave row, eave and body half widths, the roof's rise over the wall; the eaves shrink a little storey by storey and the
// sorin takes about a quarter of the height, the bottom eave about 0.3 of it
const ROOFS = [[60, 15, 6, 7], [79, 16, 7, 6], [98, 17, 8, 6], [117, 18, 9, 6], [137, 20, 10, 6]].map(([e, r, b, rise]) => ({ e, r, b, rise, up: 2 }));
const tipOf = R => ({ x: R.r + 1, y: R.e - R.up - 1 });  // the swept-up corner of an eave (x from the axis)
const BB = { x: 273, y: 122, w: 50, h: 20 };          // the billboard's face: a projecting signboard on the lane's right
const TEA = { x0: 0, x1: 153, base: 207 };            // the corner tea house (frontal, on our left)
const WALL = { x0: 327, top: 166, base: 208 };        // the temple wall on our right
const ACCENT = '#e4572e';
// The share of the crowd the scene shows by default: 1 = everyone, 0.5 = about half, just above 0 = only the cast the
// scene's story needs (the tea house girl, the shopkeeper, the geiko, the rickshaw, the couple, the maiko), and exactly
// 0 = nobody at all. render(t, { crowd }) takes any other share for one frame.
const CROWD = 0.75;

/* ================= Timeline (loop seconds) ================= */
const RICK = [{ a: 6, b: 22, dir: 1 }, { a: 86, b: 102, dir: -1 }, { a: 166, b: 182, dir: 1 }];
const GEIKO = { a: 28, turn: 40, b: 84 };             // in from the left, then up the lane
const COUPLE = { a: 92, turn: 101, stop: 108, go: 117, b: 146 };
const MAIKO = { a: 140, foot: 174, b: 188 };          // down the lane, then off to the right
const GUSTS = [44, 134, 210], GUST_LEN = 8;
const BELLS = [116, 196];
const HERON = { off: 116.5, gone: 150, back: 200, land: 218 };
const SERVE = 40, SHOJI = 24;
const CAT_WALK = [[150, 160, 1], [226, 236, -1]];

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const mixc = (a, b, t) => { const A = typeof a === 'string' ? C(a) : a, B = typeof b === 'string' ? C(b) : b; return [0, 1, 2].map(k => Math.round(lerp(A[k], B[k], t))); };
const sh = (col, k) => mixc(col, k < 1 ? '#05040c' : '#ffffff', k < 1 ? 1 - k : k - 1);   // darker (k < 1) or lighter (k > 1)
const css = c => typeof c === 'string' ? c : `rgb(${c[0]},${c[1]},${c[2]})`;
const hexOf = c => '#' + c.map(v => v.toString(16).padStart(2, '0')).join('');
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  const P = {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col, a) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) P.set(k, y, col, a); },
    line(x0, y0, x1, y1, col, th = 1) {
      const n = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
      for (let s = 0; s <= n; s++) { const X = lerp(x0, x1, s / n), Y = lerp(y0, y1, s / n); if (th > 1) P.fill(Math.round(X), Math.round(Y), th, th, col); else P.set(X, Y, col); }
    },
    /** filled ellipse; col is a colour or (i, j, dx, dy) => colour (dx, dy in -1..1) or null to skip a pixel */
    ell(cx, cy, rx, ry, col) {
      for (let j = Math.floor(cy - ry); j <= Math.ceil(cy + ry); j++) for (let i = Math.floor(cx - rx); i <= Math.ceil(cx + rx); i++) {
        const dx = (i - cx) / (rx + 0.35), dy = (j - cy) / (ry + 0.35);
        if (dx * dx + dy * dy > 1) continue;
        const v = typeof col === 'function' ? col(i, j, dx, dy) : col;
        if (v) P.set(i, j, v);
      }
    },
    /** FONT3 text at scale sc; returns the width */
    txt(s, x0, y0, col, sc = 1, gap = 1) {
      let cx = x0;
      for (const ch of s) {
        const g = FONT3[ch];
        if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') P.fill(cx + i * sc, y0 + j * sc, sc, sc, col); });
        cx += (3 + gap) * sc;
      }
      return cx - x0 - gap * sc;
    },
    /** string art: key maps a character to a colour ('.' or unknown = skip) */
    rows(g, x0, y0, key, flip = false) {
      g.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) P.set(x0 + (flip ? row.length - 1 - i : i), y0 + j, col); } });
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
  return P;
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, dx = 0, dy = 0) => ctx.drawImage(o.c, o.x + dx, o.y + dy);
const flipped = c => { const [f, x] = makeCanvas(c.width, c.height); x.translate(c.width, 0); x.scale(-1, 1); x.drawImage(c, 0, 0); return f; };
/** flattened dithered ellipse of light (pools on the stones) */
function makePool(rx, ry, col, maxA) {
  const p = pix(rx * 2 + 1, ry * 2 + 1), c = C(col);
  for (let y = 0; y <= ry * 2; y++) for (let x = 0; x <= rx * 2; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
/** a gust's strength at t: 0 most of the loop, rising and falling over GUST_LEN seconds */
const gust = t => { for (const g of GUSTS) if (t >= g && t < g + GUST_LEN) return Math.sin(Math.PI * (t - g) / GUST_LEN) ** 2; return 0; };
const gold = i => frac((i + 0.5) * 0.6180339887);

/* ================= Invented kana-like glyphs (no real words) ================= */
const G5 = [
  ['####.', '...#.', '.###.', '.#...', '.####'], ['#.#..', '#.###', '#.#.#', '..#.#', '.#.#.'], ['.#...', '####.', '.#.#.', '#..#.', '.##.#'],
  ['#.#.#', '#.#.#', '....#', '...#.', '.##..'], ['#####', '..#..', '.###.', '..#..', '.#.#.'], ['.##..', '...#.', '####.', '#..##', '.##.#'],
  ['#...#', '.###.', '..#..', '.###.', '..#..'], ['..##.', '.#..#', '..##.', '...#.', '###..'],
];
const glyphP = (p, g, x, y, col) => g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') p.set(x + i, y + j, col); });

/* ================= Sky (a clear, cold late-autumn night, the old town's glow low under the pagoda) ================= */
const SKY = ['#03061a', '#050a22', '#08102c', '#0b1534', '#0f1b3e', '#142248', '#1a2a52', '#20305a', '#283660', '#323a64', '#3e3e66', '#4c4266'];
const MOON = { x: 158, y: 36 };
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    if (y >= 160) { p.set(i, y, '#0e0c18'); continue; }
    let v = Math.pow(Math.min(1, y / 150), 1.25) * 0.62;
    v += Math.max(0, 1 - Math.hypot((i - 240) / 170, (y - 150) / 70)) * 0.42;      // the lanterns' glow over the lane
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 52)) * 0.22;  // and the moon's
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(12);
  while (STARS.length < 46) {
    const x = Math.floor(r() * W), y = 1 + Math.floor(r() * 96);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 22 || Math.abs(x - 240) < 30) continue;
    STARS.push({ x, y, k: 20 + Math.floor(r() * 50), ph: r(), b: 0.3 + r() * 0.6, big: r() < 0.08 });
  }
}
function renderMoon() {                               // a moon two nights past full, its dark limb on the left
  const p = pix(17, 17);
  p.ell(8, 8, 7, 7, (i, j, dx, dy) => {
    if (Math.hypot(i + 4.5, j - 7.5) < 11.8 && dx < -0.55) return null;
    const mare = hash(i >> 1, j >> 1, 9) < 0.28 && dx > -0.5;
    return dx + dy > 0.75 ? '#c8c0a4' : mare ? '#d4ccb0' : dx + dy < -0.5 ? '#fffbea' : '#f2ecd6';
  });
  return p.done();
}
/* ================= Clouds (tileable, moonlit from above, the town's glow under the low bank) ================= */
const CLOUD_DEFS = [
  { seed: 17, y: 2, h: 42, banks: 4, k: 1, rx: [16, 34], ry: [2, 5],
    pal: { top: '#56628e', body: '#1e2a52', core: '#1a254a', bot: '#2c3660', bot2: '#232e58' } },
  { seed: 29, y: 52, h: 54, banks: 3, k: 2, rx: [12, 24], ry: [3, 6],
    pal: { top: '#6a7098', body: '#26305a', core: '#222a52', bot: '#5c4a6e', bot2: '#3c3a64' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 60 + r() * 120, base = h * (0.45 + r() * 0.3), np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.3, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      const dy = y - q.y;
      if (dy * dy > q.ry * q.ry * 4 + 40) continue;
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.5;
      if (v > best) best = v;
    }
    D[y * W + x] = best + 0.16 * Math.sin(TAU * 11 * x / W + y * 0.5 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.3);
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    p.set(x, y, get(x, y - 1) + th <= 0.03 ? P.top : get(x, y + 1) + th <= 0.03 ? P.bot
      : get(x, y + 2) <= 0.05 + bayer(x, y) * 0.2 ? P.bot2 : v > 0.55 + th ? P.core : P.body);
  }
  return { c: p.done(), y: def.y, k: def.k };
}
function drawScroll(cv, y, k, t) { const off = scroll(t, k, W); ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); }

/* ================= Higashiyama: two ridges, the temple grounds' lit maples, a hall's roof ================= */
const ridge = (x, far) => far
  ? 92 + 7 * Math.sin(x * 0.011 + 0.6) + 4 * Math.sin(x * 0.031 + 2) + 2 * Math.sin(x * 0.083)
  : 108 + 6 * Math.sin(x * 0.014 + 2.2) + 4 * Math.sin(x * 0.041 + 1) + 2 * Math.sin(x * 0.11 + 0.4);
function renderHills() {
  const p = pix(W, 160), r = rng(41), wins = [];
  for (let x = 0; x < W; x++) {
    const yf = Math.round(ridge(x, true)), yn = Math.round(ridge(x, false) - (hash(x >> 2, 3, 1) < 0.5 ? 1 : 0) - (hash(x >> 1, 4, 1) < 0.3 ? 1 : 0));
    for (let y = yf; y < 160; y++) p.set(x, y, y === yf ? '#26325a' : pick(['#16203e', '#141c38', '#121a34'], (y - yf) / 20, x, y));
    for (let y = yn; y < 160; y++) {
      const glow = Math.max(0, 1 - Math.hypot((x - 240) / 120, (y - 150) / 40));
      p.set(x, y, y === yn ? '#1a2440' : pick(['#0a1024', '#0c1428', '#10182e', '#2a2236', '#4a2a30'], 0.35 + glow * 0.7 + (hash(x >> 1, y >> 1, 5) - 0.5) * 0.25, x, y));
    }
  }
  // the temple grounds' maples, lit from below for the autumn evening opening
  for (let k = 0; k < 26; k++) {
    const cx = 150 + r() * 180, cy = 124 + r() * 18, rx = 5 + r() * 9, ry = 3 + r() * 4;
    const lit = Math.max(0.15, 1 - Math.abs(cx - 240) / 100);
    p.ell(cx, cy, rx, ry, (i, j, dx, dy) => (hash(i, j, 8) < 0.12 ? null
      : pick(['#2a1220', '#5a1a1e', '#8e2a20', '#c4482a', '#e8823a'], lit * (0.85 - dy * 0.35) + (hash(i, j, 2) - 0.5) * 0.35, i, j)));
  }
  // the odd lit window in the trees
  for (let k = 0; k < 7; k++) wins.push({ id: k, x: Math.round(60 + r() * 360), y: Math.round(118 + r() * 14), n: 2 + Math.floor(r() * 4), off: r() });
  return { c: trim(p), wins };
}
/** mist breathing over the hills: a tileable dithered band */
function renderMist() {
  const p = pix(W, 30);
  for (let y = 0; y < 30; y++) for (let x = 0; x < W; x++) {
    const v = Math.sin(Math.PI * y / 30) * (0.5 + 0.3 * Math.sin(TAU * 3 * x / W + y * 0.2) + 0.2 * Math.sin(TAU * 7 * x / W - 1));
    if (v > 0.25 + bayer(x, y) * 0.75) p.set(x, y, v > 0.62 ? '#3e4a72' : '#2e3a62');
  }
  return p.done();
}

/* ================= Yasaka Pagoda (Hokan-ji): five wide, shallow roofs, the bronze sorin, floodlit from below ================= */
const PG = { x0: PAG.cx - 34, y0: 14, w: 69, h: 140 };
function renderPagoda() {
  const p = pix(PG.w, PG.h), cx = PAG.cx - PG.x0, Y = y => y - PG.y0;
  const S = (dx, y, col) => p.set(cx + dx, Y(y), col);
  const lift = (R, a) => a > R.r - 6 ? R.up * ((a - (R.r - 6)) / 6) ** 2 : 0;          // the eave sweeps up into its corners
  // the storeys: short walls of dark boards, corner and middle posts, a shut door, a railing on the roof below; the
  // floodlights below warm them a little towards their feet
  ROOFS.forEach((R, k) => {
    const y0 = R.e + 6, y1 = k < 4 ? ROOFS[k + 1].e - ROOFS[k + 1].rise - 1 : 152, mid = Math.round(R.b * 0.5);
    for (let y = y0; y <= y1; y++) for (let dx = -R.b - 2; dx <= R.b + 2; dx++) {
      const a = Math.abs(dx), left = dx < 0, down = (y - y0) / Math.max(1, y1 - y0);
      if (y === y1 && k < 4) { S(dx, y, a > R.b ? (left ? '#6a3a20' : '#9a5a2e') : left ? '#8a4e28' : '#c07a3e'); continue; }   // the railing
      if (a > R.b) continue;
      let col;
      if (a === R.b || a === mid) col = left ? '#40241a' : '#64381f';                                                      // posts
      else if (a <= 1 && y > y0) col = a ? '#22120c' : '#120a08';                                                         // the door, shut
      else col = pick(left ? ['#1c110b', '#28170f', '#382015'] : ['#24150d', '#382015', '#52301c'], 0.12 + down * 0.8, dx + 40, y);   // boards
      S(dx, y, col);
    }
  });
  // roofs: a gentle concave slope of dark tiles from the wall above out to the eave, the moon along its top; the
  // eave's edge, the rafters under it and the brackets on the wall glow in the floodlights
  ROOFS.forEach((R, k) => {
    const top = k ? ROOFS[k - 1].b + 1 : 4;
    for (let dx = -R.r; dx <= R.r; dx++) {
      const a = Math.abs(dx), f = clamp((a - top) / (R.r - top), 0, 1), up = lift(R, a);
      const lo = Math.round(R.e - up), u = Math.round(R.e - R.rise + (R.rise - 2) * (1 - (1 - f) * (1 - f)) - up);
      for (let y = u; y <= lo; y++) {
        let col;
        if (y === lo) col = a > R.r - 2 ? '#f6c66a' : (dx & 1) ? '#e0a050' : '#c07a3a';                   // the eave's edge
        else if (y === lo - 1) col = (dx & 1) ? '#34303e' : '#46404e';                                   // tile ends
        else if (y === u) col = dx < 0 ? '#3a4262' : '#5a6488';                                          // the moon on the tiles
        else col = (dx & 1) ? '#1c2030' : (dx < 0 ? '#22283a' : '#2a3044');
        S(dx, y, col);
      }
      if (a <= R.r - 2) S(dx, lo + 1, (dx & 1) ? '#f2b45a' : '#d88a40');                                 // rafters, receding to the wall
      if (a <= R.r - 4) S(dx, R.e + 2, (dx & 1) ? '#c87434' : '#b0602c');
      if (a <= R.r - 6) S(dx, R.e + 3, (dx & 1) ? '#8e4824' : '#7a3c20');
      if (a <= R.b + 3) S(dx, R.e + 4, (Math.floor((dx + 40) / 2) & 1) ? '#f2b45a' : '#c8642c');          // brackets
      if (a <= R.b + 2) S(dx, R.e + 5, '#5a2816');
    }
    const T = tipOf(R);
    for (const sx of [-1, 1]) { S(sx * T.x, T.y, '#f6d080'); S(sx * T.x, T.y + 1, '#a8743a'); }          // the swept-up corners
  });
  // the sorin, bronze: the roban on the top roof, bowl, lotus, nine rings, the water-flame and the jewel
  const B = (y, hw, colL, colR) => { for (let dx = -hw; dx <= hw; dx++) S(dx, y, dx < 0 ? colL : colR); };
  for (let y = 48; y <= 52; y++) B(y, 3, '#2e2c38', y === 48 ? '#7a7890' : '#4a4858');
  B(46, 3, '#5a4422', '#a8843c'); B(47, 3, '#4a3a1e', '#8a6a30'); B(45, 2, '#8a6a30', '#e0bc60'); B(44, 3, '#6a5028', '#c8a050');
  for (let y = 26; y <= 43; y++) { if ((y & 1) === 0) B(y, 2, '#6a5428', '#d0ac58'); else B(y, 0, '#4a3a1e', '#a8843c'); }
  ['.#.#.', '##.##', '.###.', '#.#.#', '..#..'].forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') S(i - 2, 21 + j, i > 2 ? '#f0cc70' : '#a8843c'); });
  B(19, 1, '#8a6a30', '#e8c068'); B(20, 1, '#6a5028', '#c8a050'); S(0, 18, '#f6d888'); S(0, 17, '#fff0b0');
  return p.done();
}
/** the same pagoda in shade: laid over it with a breathing alpha, so the floodlit gold swells and settles */
function renderPagodaShade(lit) {
  const [c, x] = makeCanvas(PG.w, PG.h);
  x.drawImage(lit, 0, 0); x.globalCompositeOperation = 'source-in'; x.fillStyle = '#100c24'; x.fillRect(0, 0, PG.w, PG.h);
  return c;
}
const BELLS_AT = ROOFS.flatMap((R, k) => [-1, 1].map(sx => ({ x: PAG.cx + sx * tipOf(R).x, y: tipOf(R).y + 2, k: k * 2 + (sx > 0 ? 1 : 0) })));
function drawPagoda(t) {
  const breath = 0.5 + 0.5 * wave(t, 6, 0.1), g = gust(t);
  halo(HL.pagoda, PAG.cx, 96, 0.75 + 0.25 * breath);
  ctx.drawImage(PAG_C, PG.x0, PG.y0);
  ctx.globalAlpha = 0.22 * (1 - breath); ctx.drawImage(PAG_SHADE, PG.x0, PG.y0); ctx.globalAlpha = 1;
  // the wind bells under each corner: they sway, more in a gust, and catch the light now and then
  for (const b of BELLS_AT) {
    const sw = Math.round((0.6 + g * 1.4) * wave(t, 40 + b.k * 3, b.k * 0.13));
    rect(ctx, b.x + sw, b.y, 1, 2, '#6a5228');
    if (hash(b.k, step(t, 960), 31) < 0.07 + g * 0.2) rect(ctx, b.x + sw, b.y + 1, 1, 1, '#fff2b0');
  }
  // the lamp-lit tip of the sorin
  halo(HL.tip, PAG.cx, 30, 0.5 + 0.2 * breath);
}

/* ================= The lane: machiya fronts on both sides, the stepped paving, the houses where it turns ================= */
// Each side is drawn column by column in perspective: a column x is a depth share s (and a depth z = 1 / s), each
// house owns a run of z, and a row's height h above the ground (px at the near end) picks its material: stone plinth,
// lit lattice or the noren door, a beam, the low upper floor (clay slats or shoji), the eaves projecting over the lane.
const HOUSES = {
  L: [
    { z: [1, 1.45], top: 98, up: 'slat', noren: '#2a3a7a', door: [0.12, 0.42], fence: 1, lit: 1 },
    { z: [1.45, 1.95], top: 92, up: 'shoji', noren: '#f0e8d8', door: [0.5, 0.8], lit: 1 },
    { z: [1.95, 2.55], top: 100, up: 'slat', noren: '#7a2a2a', door: [0.2, 0.5], fence: 1 },
    { z: [2.55, 3.3], top: 88, up: 'shoji', noren: '#3a5a4a', door: [0.55, 0.85], lit: 1 },
    { z: [3.3, 4.2], top: 96, up: 'slat', noren: '#2a3a7a', door: [0.2, 0.5] },
    { z: [4.2, 5.3], top: 102, up: 'shoji', noren: '#e8dcc8', door: [0.4, 0.7], lit: 1 },
    { z: [5.3, 6.7], top: 90, up: 'slat', noren: '#6a2a3a', door: [0.3, 0.6] },
    { z: [6.7, 8.4], top: 98, up: 'shoji', noren: '#2a3a7a', door: [0.3, 0.6], lit: 1 },
  ],
  R: [
    { z: [1, 1.32], top: 94, up: 'shoji', noren: '#f0e8d8', door: [0.3, 0.75], lit: 1 },
    { z: [1.32, 1.9], top: 100, up: 'slat', noren: '#2a4a3a', door: [0.45, 0.75], fence: 1 },
    { z: [1.9, 2.5], top: 90, up: 'shoji', noren: '#7a2a2a', door: [0.2, 0.5], lit: 1 },
    { z: [2.5, 3.2], top: 98, up: 'slat', noren: '#2a3a7a', door: [0.5, 0.8], fence: 1 },
    { z: [3.2, 4.1], top: 92, up: 'shoji', noren: '#e8dcc8', door: [0.2, 0.5], lit: 1 },
    { z: [4.1, 5.2], top: 100, up: 'slat', noren: '#6a2a3a', door: [0.5, 0.8] },
    { z: [5.2, 6.6], top: 94, up: 'shoji', noren: '#2a3a7a', door: [0.3, 0.6], lit: 1 },
    { z: [6.6, 8.4], top: 90, up: 'slat', noren: '#7a2a2a', door: [0.4, 0.7] },
  ],
};
const sideX = (side, s, off = 0) => side === 'L' ? VP.x - s * (SL - off) : VP.x + s * (SR - off);
const sOf = (side, x, off = 0) => side === 'L' ? (VP.x - x) / (SL - off) : (x - VP.x) / (SR - off);
const houseAt = (side, z) => HOUSES[side].find(hs => z >= hs.z[0] && z < hs.z[1]);
const FOG = '#161c34';
const fogged = (col, s, lit) => mixc(col, FOG, clamp((0.62 - s) / 0.55, 0, lit ? 0.35 : 0.72));
function renderLane() {
  const p = pix(W, H), wins = [];
  for (const side of ['L', 'R']) {
    const xa = Math.ceil(sideX(side, S_FAR)), xb = Math.round(sideX(side, 1)), x0 = Math.min(xa, xb), x1 = Math.max(xa, xb);
    HOUSES[side].forEach((hs, k) => { hs.win = pix(W, H); hs.k = k; });
    for (let x = x0; x <= x1; x++) {
      const s = sOf(side, x);
      if (s < S_FAR || s > 1.001) continue;
      const z = 1 / Math.min(1, s), hs = houseAt(side, z);
      if (!hs) continue;
      const u = (z - hs.z[0]) / (hs.z[1] - hs.z[0]), g = gY(s);
      const pxW = Math.abs(sideX(side, 1 / hs.z[0]) - sideX(side, 1 / hs.z[1]));   // the house's width on screen
      const slat = (n, duty) => { const per = pxW / n; return per < 2.2 ? -1 : frac(u * n) < duty ? 1 : 0; };
      for (let y = Math.floor(hY(s, hs.top)); y <= Math.ceil(g); y++) {
        const h = (g - y) / s;
        if (h < 0 || h > hs.top) continue;
        let col, lit = false;
        if (u < 0.025 || u > 0.985) col = '#0c080c';                                                       // the firewall between houses
        else if (h < 3) col = (y & 1) ? '#2a2834' : '#34303c';
        else if (h < 42) {
          const door = u >= hs.door[0] && u <= hs.door[1];
          if (door && h > 25) {                                                                           // the noren over the door
            const sl = frac((u - hs.door[0]) / (hs.door[1] - hs.door[0]) * 4);
            col = sl < 0.08 ? '#1a0e0c' : h > 39 ? sh(hs.noren, 0.7) : hs.noren;
            if (pxW > 30 && h > 31 && h < 36 && sl > 0.35 && sl < 0.65) col = hs.noren[1] > 'b' ? '#3a2a2a' : '#f0e6d0';   // a mark on each panel
          } else if (door) { col = pick(['#6a3a20', '#c07838', '#eab060'], 0.3 + h / 30, x, y); lit = true; }
          else if (hs.fence && h < 11) {                                                                  // inuyarai, the curved bamboo fence
            const bars = slat(hs.z[1] - hs.z[0] > 0.6 ? 26 : 18, 0.6);
            col = h > 9.5 ? '#9a8458' : bars === 0 ? '#1a120c' : pick(['#3a2c1a', '#6a5434'], h / 11, x, y);
          } else {                                                                                        // senbon-goshi, lit from inside
            const bars = slat(hs.z[1] - hs.z[0] > 0.6 ? 34 : 24, 0.5);
            col = bars === 1 ? '#2a1a12' : bars === 0 ? (hs.lit ? pick(['#7a4424', '#c47a3a'], 0.4 + h / 50, x, y) : '#3a2418') : pick(['#2a1a12', hs.lit ? '#a8642e' : '#3a2418'], 0.45, x, y);
            lit = !!hs.lit && bars !== 1;
          }
        } else if (h < 46) col = '#1c120e';
        else if (h < 53) col = '#0e0a0e';
        else if (h < hs.top - 6) {
          const win = u > 0.18 && u < 0.82 && h > 57 && h < hs.top - 11;
          if (hs.up === 'slat') {
            col = pick(['#3a3240', '#544850', '#6e6058'], 0.2 + (h - 53) / 40, x, y);
            if (win) { const bars = slat(9, 0.5); col = bars === 1 ? '#5a4c48' : '#1a1418'; if (bars !== 1) hs.win.set(x, y, fogged(bars ? '#d89a50' : '#ffcf80', s, true)); }
          } else {
            col = '#2a1a14';
            if (win) {
              const gx = slat(10, 0.82), gy = frac((h - 57) / 5) < 0.2;
              col = gx === 0 || gy ? '#3a2a1e' : '#6a6070';
              if (!(gx === 0 || gy)) { col = '#4a4450'; hs.win.set(x, y, fogged(gx < 0 ? '#c8a070' : '#f0d8a0', s, true)); }
            }
          }
        } else col = '#0e0a0e';
        p.set(x, y, fogged(col, s, lit));
      }
    }
    // the eaves, projecting over the lane: tiles, their lit ends, the soffit in shade
    for (const [off, hLo, hHi, which] of [[7, 45, 52, 'low'], [9, 0, 0, 'top']]) {
      for (let x = x0 - 12; x <= x1 + 12; x++) {
        const s = sOf(side, x, off);
        if (s < S_FAR || s > 1.06) continue;
        const z = 1 / Math.min(1, s), hs = houseAt(side, z);
        if (!hs) continue;
        const lo = which === 'top' ? hs.top - 6 : hLo, hi = which === 'top' ? hs.top : hHi, g = gY(s);
        const ya = Math.round(g - hi * s), yb = Math.round(g - lo * s);
        for (let y = ya; y <= yb; y++) {
          const r = yb - y;
          const col = r === 0 ? ((x & 1) ? '#8a6448' : '#5a4440') : r === 1 ? '#1a1620' : y === ya ? '#3a4060' : (x & 1) ? '#1e2030' : '#242638';
          p.set(x, y, fogged(col, s, r === 0));
        }
      }
    }
    HOUSES[side].forEach(hs => { if (hs.k % 2 === (side === 'L' ? 0 : 1) || hs.k === 3) { const o = trim(hs.win); wins.push({ ...o, id: wins.length, n: 2 + (hs.k % 3), off: hs.k * 0.37 + (side === 'L' ? 0 : 0.5) }); } });
  }
  renderLaneGround(p);
  renderFarHouses(p);
  return { c: trim(p), wins };
}
// the paving: slabs in courses that shrink with distance, steps every few metres, drains at the sides, fallen leaves
const STEPS_Z = [1.55, 2.6, 3.9, 5.5];
function renderLaneGround(p) {
  const r = rng(53);
  for (let y = 150; y <= 207; y++) {
    const s = Math.min(1.04, (y - VP.y) / 62), z = 1 / s, zn = 1 / Math.min(1.04, (y + 1 - VP.y) / 62);
    const course = Math.floor(z * 3.4), joint = course !== Math.floor(zn * 3.4);
    const stepI = STEPS_Z.findIndex(sz => z >= sz && zn < sz), riser = STEPS_Z.some(sz => z < sz && z > sz - 0.05 * sz * sz);
    for (let x = Math.floor(VP.x - s * SL) - 1; x <= Math.ceil(VP.x + s * SR) + 1; x++) {
      if (p.alpha(x, y)) continue;
      const X = (x - VP.x) / s, edge = Math.abs(X) > (X < 0 ? SL : SR) - 6;
      const off = (course & 1) * 13, sl = Math.floor((X + 200 + off) / 26), sl2 = Math.floor((X + 200 + off + 1 / s) / 26);
      let col;
      if (stepI >= 0) col = '#6a6a80';
      else if (riser) col = '#141420';
      else if (edge) col = Math.abs(X) > (X < 0 ? SL : SR) - 2 ? '#24222e' : '#0e0e16';
      else if (joint || (sl !== sl2 && s > 0.3)) col = '#15161f';
      else {
        const v = hash(sl, course, 7), wet = Math.max(0, 1 - Math.abs(X) / 70) * 0.3;
        col = pick(['#181a24', '#1e202c', '#262836', '#303244'], 0.3 + v * 0.35 + wet, x, y);
      }
      p.set(x, y, fogged(col, s, false));
    }
  }
  for (let k = 0; k < 120; k++) {                     // fallen maple leaves, drifted to the sides and against the steps
    const s = 0.2 + Math.pow(r(), 0.7) * 0.82, side = r() < 0.5 ? -1 : 1, lat = side * (1 - Math.pow(r(), 2.2) * 0.9);
    const x = Math.round(VP.x + lat * s * (side < 0 ? SL - 8 : SR - 8)), y = Math.round(gY(Math.min(1.03, s)));
    const col = ['#8a2a1e', '#b8361e', '#d4542a', '#e07a30', '#6a2018'][Math.floor(r() * 5)];
    p.set(x, y, fogged(col, s, false)); if (s > 0.55) p.set(x + 1, y, fogged(sh(col, 0.8), s, false));
    if (s > 0.8 && r() < 0.5) p.set(x, y - 1, col);
  }
}
// where the lane turns, under the pagoda: two low houses facing us, a lit doorway, the tiled roofs that hide the
// pagoda's lowest storey
function renderFarHouses(p) {
  for (const [a, b, top, door] of [[221, 241, 140, 228], [239, 260, 138, 251]]) {
    for (let x = a; x <= b; x++) {
      const ridgeY = top - 2 + Math.round(Math.abs(x - (a + b) / 2) * 0.12);
      for (let y = ridgeY; y < 151; y++) {
        if (p.alpha(x, y)) continue;
        let col = y === ridgeY ? '#3a4262' : y < top + 3 ? '#1c1e2c' : y === top + 3 ? '#7a5a40' : '#1a1418';
        if (y > top + 5 && Math.abs(x - door) <= 1) col = '#e8a858';
        else if (y > top + 5 && y < top + 9 && (x - a) % 5 === 2) col = '#a8683a';
        p.set(x, y, col);
      }
    }
  }
}
/* the lane's lanterns: one per house, hanging beside the door under the low eave */
const LANE_LANTERNS = [];
for (const side of ['L', 'R']) HOUSES[side].forEach((hs, k) => {
  const z = lerp(hs.z[0], hs.z[1], hs.door[0] > 0.3 ? hs.door[0] - 0.08 : hs.door[1] + 0.08), s = 1 / z;
  LANE_LANTERNS.push({ id: LANE_LANTERNS.length, x: Math.round(sideX(side, s, 6)), y: Math.round(hY(s, 41)), s, red: (k + (side === 'R' ? 1 : 0)) % 3 !== 0 });
});
function renderChochin(h, red) {                       // a paper lantern h rows tall: ribs, a glyph band, black caps
  const w = Math.max(1, Math.round(h * 0.7)), p = pix(w + 2, h + 2), cx = (w + 1) / 2;
  for (let y = 1; y <= h; y++) {
    const v = (y - 1) / Math.max(1, h - 1), hw = Math.max(0.5, (w / 2) * Math.sqrt(Math.max(0.15, 1 - Math.pow(v * 2 - 1, 2))));
    for (let i = 0; i < w + 2; i++) {
      const dx = (i - cx) / hw;
      if (Math.abs(dx) > 1) continue;
      let col = red ? pick(['#7a1410', '#d02a1c', '#ff6a3a', '#ffb070'], 1 - Math.abs(dx) * 0.8 - (h > 6 && y % 3 === 0 ? 0.25 : 0), i, y)
        : pick(['#a8783a', '#f0c070', '#fff0c8'], 1 - Math.abs(dx) * 0.7 - (h > 6 && y % 3 === 0 ? 0.2 : 0), i, y);
      if (h >= 8 && h < 12 && Math.abs(dx) < 0.45 && v > 0.3 && v < 0.75 && hash(i, y, h) < 0.55) col = red ? '#2a0c0a' : '#3a1a10';
      p.set(i, y, col);
    }
  }
  if (h >= 12) glyphP(p, G5[6], Math.round(cx - 2), Math.round(h / 2 - 1), red ? '#2a0c0a' : '#3a1a10');
  p.fill(Math.round(cx - w / 3), 0, Math.max(1, Math.round(w * 0.66)), 1, '#140c0c'); p.fill(Math.round(cx - w / 3), h + 1, Math.max(1, Math.round(w * 0.66)), 1, '#140c0c');
  return p.done();
}
function drawLaneLanterns(t, g) {
  for (const l of LANE_LANTERNS) {
    const h = Math.max(2, Math.round(10 * l.s)), spr = (l.red ? CHO_R : CHO_W)[Math.min(10, h)];
    const sw = Math.round(g * 1.2 * l.s * wave(t, 48, l.id * 0.21));
    const fl = 0.75 + 0.15 * wave(t, 300 + l.id * 17, l.id * 0.3) + 0.1 * (hash(l.id, step(t, 2400), 3) - 0.5);
    rect(ctx, l.x, l.y - 2, 1, 2, '#1a120e');
    ctx.drawImage(spr, l.x + sw - (spr.width >> 1), l.y);
    halo(l.s > 0.6 ? HL.lantern : l.s > 0.3 ? HL.lanternM : HL.lanternS, l.x + sw, l.y + (h >> 1), fl);
  }
}
const LANE_POOLS = LANE_LANTERNS.filter(l => l.s > 0.22).map(l => ({ id: l.id, x: Math.round(lerp(l.x, VP.x, 0.3)), y: Math.round(gY(l.s)) - 1, s: l.s }));
function drawLanePools(t) {
  for (const pl of LANE_POOLS) {
    const pool = pl.s > 0.6 ? POOL.laneL : pl.s > 0.38 ? POOL.laneM : POOL.laneS;
    drawPool(pool, pl.x, pl.y, 0.55 + 0.12 * wave(t, 300 + pl.id * 17, pl.id * 0.3) + 0.15 * hash(pl.id, step(t, 480), 5));
  }
}
function drawLaneWins(t) {
  for (const w of LANE.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 9) < 0.7) blit(w);
}

/* ================= The billboard: a sweet shop's projecting signboard, under its own little roof ================= */
// The city's own ad: MATCHA in cream on green lacquer, a bowl whose whisk dips every 4 s and steams, a maple leaf, and
// a ticker of what the shop sells: whole words of up to 8 letters, centred in its window, each fading in, held and
// fading out before the next, so no moment shows a broken word. render(t, { pitch }) paints the site's faces over it;
// { billboard: false } leaves out the board, its roof, its iron arm and its lamps, and the house front behind it shows.
const BB_LAMPS = [11, 38], TICK = ['PARFAIT', 'WARABI', 'MOCHI', 'TSUKIYO', 'EST 1890'], TICK_T = 4, TICK_FADE = 0.35;   // 20 s a round, 12 a loop
const TICK_BOX = { x: 16, y: 12, w: 31, h: 5 };        // the window, on the face
function renderBBFrame() {
  const p = pix(W, H), x0 = BB.x - 2, x1 = BB.x + BB.w + 1, y0 = BB.y - 2, y1 = BB.y + BB.h + 1;
  p.fill(x0, y0, x1 - x0 + 1, y1 - y0 + 1, '#1a0e0c');
  for (let i = x0; i <= x1; i++) { p.set(i, y0, '#5a3424'); p.set(i, y1, '#2a1610'); }
  p.fill(x0 - 2, y0, 2, y1 - y0 + 1, '#3a2218'); p.fill(x0 - 2, y0, 2, 1, '#6a4430');                       // the board's edge, seen from the lane
  for (let i = x0 - 4; i <= x1 + 3; i++) {                                                                // its little tiled roof
    const up = i < x0 || i > x1 ? 1 : 0;
    p.set(i, y0 - 4 - up, '#3a4262'); p.set(i, y0 - 3 - up, (i & 1) ? '#1e2030' : '#262a3c'); p.set(i, y0 - 2 - up, (i & 1) ? '#9a6a44' : '#6a4a38');
  }
  p.fill(x1 + 1, y0 - 6, 4, 1, '#2a2a34'); p.fill(x1 + 4, y0 - 8, 1, 30, '#22222c');                          // the iron arm off the house
  for (const lx of BB_LAMPS) { p.fill(BB.x + lx, y0 - 1, 1, 1, '#3a3258'); p.fill(BB.x + lx - 1, y0 - 1, 3, 1, '#4a4258'); }
  for (const bx of [x0 + 6, x1 - 6]) p.fill(bx, y1 + 1, 1, 2, '#1a0e0c');
  return trim(p);
}
function renderBBAd() {
  const p = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    const lamp = Math.max(...BB_LAMPS.map(lx => 1 - Math.hypot((i - lx) / 16, j / 22)));
    p.set(i, j, pick(['#0e2418', '#16321f', '#1e4228', '#2a5634'], 0.15 + 0.85 * Math.max(0, lamp), i, j));
  }
  for (let i = 0; i < BB.w; i++) { p.set(i, 0, '#c8a050'); p.set(i, BB.h - 1, '#6a5028'); }
  p.txt('MATCHA', 17, 2, '#0a1a10'); p.txt('MATCHA', 16, 2, '#f6ecd0');
  p.fill(16, 8, 23, 1, '#c8a050');
  p.fill(15, 11, 33, 7, '#08140c');
  // the bowl: dark raku glaze, a lip of green foam
  p.fill(3, 13, 11, 1, '#9ac860'); p.fill(2, 14, 13, 1, '#5a3424'); p.fill(3, 15, 11, 1, '#4a2a1e'); p.fill(4, 16, 9, 1, '#3a2018'); p.fill(6, 17, 5, 1, '#c8a050');
  p.set(5, 13, '#d0f080'); p.set(9, 13, '#d0f080');
  // a maple leaf, top right
  p.rows(['..#..', '#.#.#', '.###.', '#####', '..#..'], 43, 2, { '#': ACCENT });
  const tick = TICK.map(word => { const q = pix(TICK_BOX.w, TICK_BOX.h); q.txt(word, (TICK_BOX.w - (word.length * 4 - 1)) >> 1, 0, '#f0c050'); return q.done(); });
  return { ad: p.done(), tick };
}
function drawBillboard(t, pitch) {
  blit(BBF);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(BBAD.ad, BB.x, BB.y);
    const q = t / TICK_T + 0.5, held = frac(q) * TICK_T, a = clamp(Math.min(held, TICK_T - held) / TICK_FADE, 0, 1);   // words change at 2, 6, 10 ... s
    ctx.globalAlpha = a; ctx.drawImage(BBAD.tick[Math.floor(q) % TICK.length], BB.x + TICK_BOX.x, BB.y + TICK_BOX.y); ctx.globalAlpha = 1;
    const u = frac(t / 4), dip = u < 0.2 ? u / 0.2 : u < 0.55 ? 1 : u < 0.7 ? 1 - (u - 0.55) / 0.15 : 0;
    const wx = BB.x + 9 + (dip >= 1 ? Math.round(wave(t, 480)) : 0), wy = BB.y + 5 + Math.round(dip * 5);   // the whisk dips, whisks, lifts
    rect(ctx, wx, wy, 1, 4, '#d8c090'); rect(ctx, wx - 1, wy + 4, 3, 2, '#e8d8a8');
    for (let k = 0; k < 3; k++) {                       // steam curling off the bowl
      const f = frac(t / 2.4 + k / 3), sx = BB.x + 5 + k * 2 + Math.round(Math.sin(TAU * (f + k * 0.3)) * 1.2), sy = BB.y + 12 - Math.round(f * 9);
      if (f < 0.85) { ctx.globalAlpha = 0.7 * (1 - f); rect(ctx, sx, sy, 1, 1, '#e8f0e0'); ctx.globalAlpha = 1; }
    }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {
    rect(ctx, BB.x + lx - 1, BB.y - 3, 3, 1, '#fff2d6');
    halo(HL.bbLamp, BB.x + lx, BB.y - 2, 0.85 + 0.1 * wave(t, 1700 + k * 130, k * 0.3));
  });
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + 8, 0.8);
}

/* ================= The corner tea house (left): counter window, noren door, lattice, the red parasol and bench ================= */
const TEA_COUNTER = { x0: 3, x1: 34, top: 166, sill: 187 }, TEA_DOOR = { x0: 39, x1: 71 }, SHOJI_WIN = { x0: 50, x1: 98, y0: 118, y1: 143 };
const BENCH = { x0: 80, x1: 124, seat: 198 }, PARASOL = { x: 114, top: 150, y: 162, r: 33 };
function renderTea() {
  const p = pix(W, H);
  for (let x = 0; x <= TEA.x1; x++) {
    // roof edge, soffit and rafters
    p.set(x, 98, '#3a4262'); p.set(x, 99, '#2a3048');
    for (let y = 100; y <= 105; y++) p.set(x, y, (x & 1) ? '#1e2030' : '#262a3c');
    p.set(x, 106, (x & 1) ? '#6a5446' : '#4a3c3a');
    for (let y = 107; y <= 110; y++) p.set(x, y, x % 5 === 0 && y < 110 ? '#3a2a20' : '#140e0e');
    // the low upper floor: plaster lit from below, a slatted mushiko-mado and a shoji window
    for (let y = 111; y <= 149; y++) {
      let col = pick(['#2e2834', '#3e3440', '#564850', '#76645c'], (y - 111) / 40, x, y);
      if (x >= 10 && x <= 40 && y >= 120 && y <= 141) col = (x - 10) % 4 < 2 ? '#5e5048' : '#1a1418';
      if (x >= SHOJI_WIN.x0 - 2 && x <= SHOJI_WIN.x1 + 2 && y >= SHOJI_WIN.y0 - 2 && y <= SHOJI_WIN.y1 + 2) col = '#2a1a12';
      if (x === 0 || x === TEA.x1 || x === TEA.x1 - 1) col = '#1a120e';
      p.set(x, y, col);
    }
    // the low eave: tiles, their lit ends, the soffit
    for (let y = 150; y <= 156; y++) p.set(x, y, y === 150 ? '#3a4262' : (x & 1) ? '#1e2030' : '#262a3c');
    p.set(x, 157, (x & 1) ? '#c8905a' : '#8a6040');
    for (let y = 158; y <= 160; y++) p.set(x, y, x % 6 === 0 && y < 160 ? '#4a3424' : '#120c0c');
    // the ground floor
    for (let y = 161; y <= 206; y++) {
      let col;
      if (y >= 203) col = (x + (y & 1) * 3) % 7 ? '#2e2c36' : '#1c1a22';                                   // the stone plinth
      else if (x <= 1 || (x >= 35 && x <= 38) || (x >= 72 && x <= 74) || x >= TEA.x1 - 2) col = x >= TEA.x1 - 2 ? '#1a120e' : '#2a1a12';   // posts
      else if (x >= TEA_COUNTER.x0 && x <= TEA_COUNTER.x1 && y >= TEA_COUNTER.top && y < TEA_COUNTER.sill) {
        col = y < TEA_COUNTER.top + 2 ? '#2a1a12' : pick(['#8a4e26', '#c88440', '#eab060'], 0.3 + (y - TEA_COUNTER.top) / 26, x, y);   // the lit counter window
        if (y > 176 && y < 181 && x > 6 && x < 32) col = (x % 4 === 1) ? '#3a2a1e' : '#d8b080';              // tea tins on a shelf
      } else if (x >= TEA_COUNTER.x0 - 1 && x <= TEA_COUNTER.x1 + 1 && y >= TEA_COUNTER.sill && y <= TEA_COUNTER.sill + 2) col = y === TEA_COUNTER.sill ? '#b8844e' : '#5a3a22';   // the counter
      else if (x >= TEA_DOOR.x0 && x <= TEA_DOOR.x1) col = pick(['#5a3018', '#a8642e', '#e8a858'], 0.25 + (y - 161) / 40, x, y);   // the doorway, warm inside
      else if (x > 74) col = (x - 75) % 3 === 0 ? '#2a1a12' : pick(['#6a3a1c', '#b87036'], 0.3 + (y - 161) / 50, x, y);   // senbon-goshi
      else col = '#3a2418';
      p.set(x, y, col);
    }
  }
  // a standing signboard by the corner post: dark wood, three invented glyphs in cream
  p.fill(136, 166, 10, 34, '#2a1a12'); p.fill(136, 166, 10, 1, '#5a3a24'); p.fill(137, 200, 1, 7, '#1a100c'); p.fill(144, 200, 1, 7, '#1a100c');
  [2, 6, 4].forEach((gi, k) => glyphP(p, G5[gi], 139, 170 + k * 9, '#e8dcc0'));
  // the noren's top, hanging from its rod: four indigo panels, two with a white mark (an invented crest, a leaf in a ring)
  for (let k = 0; k < 4; k++) {
    const x0 = NOREN_X[k], x1 = NOREN_X[k + 1] - 2;
    for (let x = x0; x <= x1; x++) for (let y = 161; y <= 175; y++) p.set(x, y, y === 161 ? '#141a3a' : pick(['#1a2450', '#24306a', '#2e3c7c'], 0.5 + (y - 161) / 40, x, y));
    if (k === 1 || k === 2) glyphP(p, NOREN_MARK, x0 + (k === 1 ? 2 : 1), 166, '#e8e0cc');
  }
  return trim(p);
}
const NOREN_X = [0, 1, 2, 3, 4].map(k => TEA_DOOR.x0 + Math.round(k * (TEA_DOOR.x1 - TEA_DOOR.x0 + 2) / 4));
const NOREN_MARK = ['.###.', '#.#.#', '##.##', '#.#.#', '.###.'];
function drawNoren(t, g) {                             // the lower half sways and lifts in the wind (and when someone passes)
  const lift = g * 5 + 0.5 * (1 + wave(t, 30, 0.2));
  for (let k = 0; k < 4; k++) {
    const x0 = NOREN_X[k], w = NOREN_X[k + 1] - 1 - x0, sw = Math.round((g * 2.2 + 0.5) * wave(t, 36 + k * 4, k * 0.25));
    const hem = 181 - Math.round(lift * (0.6 + 0.4 * ((k + 1) & 1)));
    rect(ctx, x0 + sw, 176, w, Math.max(1, hem - 176), '#2a3672');
    rect(ctx, x0 + sw, hem, w, 1, '#141a3a');
  }
}
function renderParasol() {                             // the nodate-gasa: a shallow red dome on a bamboo pole, the bench under it
  const p = pix(W, H), { x, top, y, r } = PARASOL;
  for (let i = -r; i <= r; i++) {
    const a = Math.abs(i) / r, yt = Math.round(top + (y - top) * a * a);
    for (let j = yt; j <= y + 1 - Math.round(a * 2); j++) {
      const rib = (i + 64) % 6 === 0, under = j > y - 2 + Math.round(-a * 2);
      p.set(x + i, j, under ? (rib ? '#5a1010' : '#8a1a14') : rib ? '#7a1410' : pick(['#9a1a16', '#c82a1e', '#e8482a'], 0.25 + (j - yt) / 12 + (i > 0 ? 0.15 : -0.05), x + i, j));
    }
  }
  p.fill(x - 1, top - 2, 3, 2, '#2a1a10');
  for (let j = y + 1; j <= BENCH.seat + 9; j++) p.set(x, j, j % 6 ? '#b8a060' : '#7a6a3a');                // the pole
  // the bench: red felt over a low wooden frame
  for (let i = BENCH.x0; i <= BENCH.x1; i++) {
    p.set(i, BENCH.seat - 1, '#e8482a'); p.set(i, BENCH.seat, '#c02a1e'); p.set(i, BENCH.seat + 1, '#8a1a14'); p.set(i, BENCH.seat + 2, '#5a1210');
  }
  for (const lx of [BENCH.x0 + 2, BENCH.x1 - 2]) p.fill(lx, BENCH.seat + 3, 1, 207 - BENCH.seat - 3, '#3a2418');
  return trim(p);
}
const TEA_LANTERNS = [{ x: 36, y: 162 }, { x: 74, y: 162 }];

/* ================= The temple wall (right): coping tiles, five white lines, a stone lantern ================= */
const TORO = { x: 350, base: 207 };
function renderWall() {
  const p = pix(W, H);
  for (let x = WALL.x0; x < W; x++) {
    const lit = clamp(1 - (x - TORO.x) / 120, 0.1, 1), dist = (x - WALL.x0) / 120;
    p.set(x, WALL.top - 8, '#3a4262'); p.set(x, WALL.top - 7, '#2a3048');
    for (let y = WALL.top - 6; y <= WALL.top - 2; y++) p.set(x, y, (x & 1) ? '#1c1e2c' : '#262a3c');
    p.set(x, WALL.top - 1, (x & 1) ? mixc('#7a6050', '#2a2430', 1 - lit) : '#2a2430');
    p.set(x, WALL.top, '#140e10'); p.set(x, WALL.top + 1, '#1e1618');
    for (let y = WALL.top + 2; y <= 199; y++) {
      let col = pick(['#2e2420', '#5a4632', '#8a6e4a', '#b8966a'], lit * (0.4 + (y - WALL.top) / 70) + (hash(x >> 1, y >> 1, 3) - 0.5) * 0.15, x, y);
      if ([172, 178, 184, 190, 196].includes(y)) col = mixc('#e8e0d0', '#3a3440', 1 - lit * 0.8);       // the five white lines
      else if ([173, 179, 185, 191, 197].includes(y)) col = sh(css(col), 0.6);
      if (x === WALL.x0 || x === WALL.x0 + 1) col = '#1a1418';
      p.set(x, y, col);
    }
    for (let y = 200; y <= 207; y++) {                 // stone footing
      const course = y < 204 ? 0 : 1, joint = (x + course * 7) % 14 === 0 || y === 203 || y === 207;
      p.set(x, y, joint ? '#14141c' : pick(['#24242e', '#3a3a48'], lit * 0.8 - dist * 0.1, x, y));
    }
  }
  // the stone lantern: hoju, kasa roof, the lit firebox, shaft, base
  const { x } = TORO, sb = TORO.base;
  p.fill(x - 7, sb - 3, 15, 3, '#4a4a58'); p.fill(x - 7, sb - 3, 15, 1, '#6a6a7a');
  p.fill(x - 2, sb - 16, 5, 13, '#4a4a58'); p.fill(x - 2, sb - 16, 1, 13, '#5e5e70'); p.fill(x + 2, sb - 16, 1, 13, '#32323e');
  p.fill(x - 5, sb - 18, 11, 2, '#5a5a6a');
  p.fill(x - 4, sb - 26, 9, 8, '#3e3e4c'); p.fill(x - 2, sb - 24, 5, 4, '#ffcf7a'); p.fill(x - 1, sb - 23, 3, 2, '#fff2c0');
  for (let i = -8; i <= 8; i++) { const yy = sb - 29 + Math.round(Math.abs(i) * 0.25); p.fill(x + i, yy - Math.round((8 - Math.abs(i)) * 0.35), 1, 3 + Math.round((8 - Math.abs(i)) * 0.35), Math.abs(i) > 6 ? '#6a6a7a' : '#4e4e5e'); }
  p.fill(x - 1, sb - 34, 3, 2, '#5a5a6a'); p.set(x, sb - 35, '#6a6a7a');
  return trim(p);
}

/** the big maple's shadow on the wall, thrown by the stone lantern: dappled, swaying with the tree */
function renderDapple() {
  const w = W - WALL.x0 + 8, h = 30, p = pix(w, h), r = rng(91);
  for (let k = 0; k < 22; k++) {
    const cx = 10 + r() * (w - 20), cy = 4 + r() * (h - 8), rx = 4 + r() * 9, ry = 2 + r() * 4;
    p.ell(cx, cy, rx, ry, (i, j, dx, dy) => (Math.hypot(dx, dy) > 0.6 && hash(i, j, 13) < 0.5 ? null : '#06040c'));
  }
  return p.done();
}
function drawDapple(t, g) {
  const dx = Math.round(1.6 * wave(t, 8, 0.3) + g * 3 * wave(t, 120, 0.1)), dy = Math.round(0.6 * wave(t, 11, 0.6));
  ctx.save(); ctx.beginPath(); ctx.rect(WALL.x0 + 2, WALL.top + 2, W - WALL.x0 - 2, 198 - WALL.top); ctx.clip();
  ctx.globalAlpha = 0.32; ctx.drawImage(DAPPLE_C, WALL.x0 - 4 + dx, WALL.top + 3 + dy); ctx.globalAlpha = 1;
  ctx.restore();
}

/* ================= Maples: trunks and branches baked, the leaf clusters swaying (more in a gust) ================= */
const MAPLE_RAMPS = [
  ['#2a0a12', '#5a1418', '#8e2220', '#c03a22', '#e4602a', '#f6a048'],
  ['#2a1210', '#64281a', '#a0461e', '#d8742a', '#f2a23a', '#fcd060'],
  ['#2a0810', '#4a1020', '#7a1a26', '#ae2a2a', '#d84a32', '#f07a4a'],
];
const TREES = [
  { id: 'R', x: 474, base: 162, top: 14, w: 130, lean: -0.32, n: 40, rx: [11, 20], ry: [7, 13], lit: 0.65 },
  { id: 'L', x: 70, base: 104, top: 50, w: 104, lean: 0.2, n: 26, rx: [9, 16], ry: [5, 9], lit: 0.55 },
  { id: 'l1', x: 206, base: 134, top: 112, w: 26, lean: 0, n: 5, rx: [4, 7], ry: [3, 5], lit: 0.8 },
  { id: 'r1', x: 282, base: 128, top: 104, w: 30, lean: 0, n: 5, rx: [4, 8], ry: [3, 5], lit: 0.75 },
];
function renderTree(T, seed) {
  const r = rng(seed), wood = pix(W, H), clusters = [], spots = [];
  const trunkTop = lerp(T.base, T.top, 0.5), tx = T.x + T.lean * (T.base - trunkTop);
  if (T.w > 60) {
    for (let j = 0; j <= 1; j += 0.01) {             // a trunk that bends as it rises, bark lit on its lantern side
      const y = lerp(T.base + 8, trunkTop, j), x = lerp(T.x, tx, j) + Math.sin(j * 3.2) * 6, w = Math.round(lerp(8, 4, j));
      for (let i = 0; i < w; i++) wood.set(x - (w >> 1) + i, y, i === 0 ? '#5a3424' : i === 1 ? '#3a2018' : hash(i, Math.round(y), 3) < 0.2 ? '#2a1814' : '#160c0e');
    }
    for (let k = 0; k < 7; k++) {                      // branches fanning up and out; the leaves hide their ends
      const a = -Math.PI / 2 + (k - 3) * 0.4 + T.lean * 0.6, len = T.w * (0.3 + r() * 0.16);
      const ex = tx + Math.cos(a) * len, ey = trunkTop + Math.sin(a) * len * 0.75;
      wood.line(tx, trunkTop, lerp(tx, ex, 0.8), lerp(trunkTop, ey, 0.8), '#1e1214', k === 3 ? 3 : 2);
      for (const f of [0.55, 0.8, 1.05]) spots.push([lerp(tx, ex, f) + (r() - 0.5) * 10, lerp(trunkTop, ey, f) + (r() - 0.5) * 8]);
    }
  }
  while (spots.length < T.n) { const ang = r() * TAU, d = Math.sqrt(r()); spots.push([tx + Math.cos(ang) * d * T.w * 0.5, lerp(T.top + 12, trunkTop + 6, r()) + Math.sin(ang) * 5]); }
  spots.sort((a, b) => a[1] - b[1]);
  spots.forEach(([sx, sy], k) => {
    const cx = Math.round(sx), cy = Math.round(sy);
    const rx = lerp(T.rx[0], T.rx[1], r()), ry = lerp(T.ry[0], T.ry[1], r()), ramp = MAPLE_RAMPS[Math.floor(r() * 3)];
    const p = pix(Math.ceil(rx) * 2 + 3, Math.ceil(ry) * 2 + 3), ox = Math.ceil(rx) + 1, oy = Math.ceil(ry) + 1;
    const below = clamp((cy - T.top) / (T.base - T.top + 1), 0, 1);
    p.ell(ox, oy, rx, ry, (i, j, dx, dy) => {
      const dd = Math.hypot(dx, dy);
      if (dd > 0.7 && hash(i + cx, j + cy, 11) < (dd - 0.7) * 2.2) return null;
      const v = T.lit * (0.3 + dy * 0.3 + below * 0.35) + (hash(i + cx, j + cy, 4) - 0.5) * 0.5 + (dx + dy < -0.9 ? 0.12 : 0);
      return pick(ramp, v, i, j);
    });
    clusters.push({ c: p.done(), x: cx - ox, y: cy - oy, k: 6 + Math.floor(r() * 8), ph: r(), amp: 0.5 + (1 - below) * 1.2, id: k });
  });
  return { wood: trim(wood), clusters };
}
function drawTree(TR, t, g) {
  blit(TR.wood);
  for (const c of TR.clusters) {
    const dx = Math.round(c.amp * (wave(t, c.k, c.ph) + g * 2.2 * wave(t, 120, c.ph))), dy = Math.round(0.4 * c.amp * wave(t, c.k + 3, c.ph + 0.3));
    ctx.drawImage(c.c, c.x + dx, c.y + dy);
  }
}

/* ================= The landing: wet slabs in perspective, puddles, fallen leaves ================= */
const PUDDLES = [{ x: 168, y: 236, rx: 34, ry: 4 }, { x: 304, y: 229, rx: 30, ry: 3 }, { x: 52, y: 226, rx: 24, ry: 3 }, { x: 236, y: 218, rx: 18, ry: 2 }, { x: 410, y: 240, rx: 36, ry: 4 }, { x: 214, y: 252, rx: 40, ry: 4 }];
const inPuddle = (x, y) => PUDDLES.some(q => ((x - q.x) / q.rx) ** 2 + ((y - q.y) / q.ry) ** 2 < 1 - hash(x, y, 2) * 0.15);
function renderLanding() {
  const p = pix(W, H - LAND), r = rng(61), rowsY = [LAND, 214, 222, 232, 245, 261, 280];
  for (let y = LAND; y < H; y++) {
    const s = (y - VP.y) / 62, ci = rowsY.findIndex((ry, k) => y >= ry && y < rowsY[k + 1]);
    for (let x = 0; x < W; x++) {
      const X = (x - VP.x) / s, off = (ci & 1) * 17, sl = Math.floor((X + 600 + off) / 34), sl2 = Math.floor((X + 600 + off + 1 / s) / 34);
      let col;
      if (y === LAND) col = x > VP.x - SL - 2 && x < VP.x + SR + 2 ? '#5a5a70' : '#1a1820';                                     // the lip of the lane's first step
      else if (rowsY.includes(y) || sl !== sl2) col = '#14141e';
      else if (inPuddle(x, y)) col = '#0a0c18';
      else col = pick(['#1e202c', '#262836', '#2e3040', '#383a4e'], 0.25 + hash(sl, ci, 5) * 0.35 + Math.max(0, 1 - Math.abs(x - 240) / 260) * 0.2 - (y - LAND) / 300, x, y);
      p.set(x, y - LAND, col);
    }
  }
  for (let k = 0; k < 90; k++) {                       // fallen leaves, more along the edges and under the trees
    const edge = r(), x = Math.round(edge < 0.35 ? r() * 140 : edge < 0.7 ? 340 + r() * 140 : r() * W), y = Math.round(LAND + 2 + Math.pow(r(), 1.3) * 58);
    if (inPuddle(x, y)) continue;
    const col = ['#8a2a1e', '#b8361e', '#d4542a', '#e07a30', '#6a2018', '#c8a040'][Math.floor(r() * 6)], big = y > 244 && r() < 0.5;
    p.set(x, y - LAND, col); p.set(x + 1, y - LAND, sh(col, 0.75));
    if (big) { p.set(x, y - LAND - 1, sh(col, 1.2)); p.set(x - 1, y - LAND, col); }
  }
  return p.done();
}

/* ================= Maple leaves: the city's own falling kind, 3 depths, flutter and flip ================= */
const LEAF_COLS = [
  { base: '#c8281c', dark: '#7a1210', light: '#f0583a', back: '#9a1e16', vein: '#e04830' },
  { base: '#e05a20', dark: '#8a2e12', light: '#f8903a', back: '#b04418', vein: '#f07a30' },
  { base: '#a8162a', dark: '#5a0a16', light: '#d83a44', back: '#801224', vein: '#c42a36' },
  { base: '#e89a2a', dark: '#9a5a14', light: '#fcc858', back: '#c07a20', vein: '#f0b040' },
];
const LEAF_SHAPES = [
  ['11', '01'],
  ['10101', '01110', '11111', '01110', '00100'],
  ['0001000', '1001001', '1101011', '0111110', '1111111', '0011100', '0001000'],
];
function makeLeaf(lc, size, back) {
  const S = LEAF_SHAPES[size], w = S[0].length, h = S.length, mid = w >> 1;
  const [c, x] = makeCanvas(w, h);
  S.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] === '1') {
    let col = back ? lc.back : lc.base;
    if (size > 0 && i === mid && j > 0) col = back ? lc.vein : lc.dark;
    else if (!back && size > 0 && i < mid && (i + j) % 3 === 0) col = lc.light;
    else if (size === 2 && j === h - 1) col = lc.dark;
    x.fillStyle = col; x.fillRect(i, j, 1, 1);
  } });
  return c;
}
const LEAVES = [[], [], []];
{
  const r = rng(4343);
  [[34, 7, 10, 4, 24, 206], [22, 10, 13, 7, 40, 236], [11, 13, 16, 12, 60, H + 24]].forEach(([count, nMin, nMax, amp, drift, land], size) => {
    for (let i = 0; i < count; i++) LEAVES[size].push({
      id: size * 100 + i, size, land, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
      ks: 36 + Math.floor(r() * 30), p2: r(), kr: 20 + Math.floor(r() * 25), p3: r(),
      kf: 26 + Math.floor(r() * 40), p4: r(), amp: amp * (0.7 + r() * 0.6), drift: drift * (0.5 + r()), col: Math.floor(r() * 4),
    });
  });
}
function drawLeaf(l, t) {
  const { cyc, u } = cycle(t, l.n, l.ph);
  const S = LEAF_SHAPES[l.size], w = S[0].length, h = S.length, sw = wave(t, l.ks, l.p2);
  const yEnd = l.size === 2 ? l.land : l.land - hash(l.id, cyc, 6) * 30;
  const y = -12 + u * (yEnd + 12) - Math.abs(sw) * (l.size + 1) * 1.5;
  const x = -30 + hash(l.id, cyc, 5) * (W + 20) + u * l.drift + sw * l.amp;
  if (x < -12 || x > W + 12) return;
  const fl = Math.cos(TAU * (l.kf * t / LOOP + l.p4)), spr = LEAF_SPR[(l.col + cyc) % 4][l.size][fl < 0 ? 1 : 0];
  ctx.globalAlpha = l.size < 2 && u > 0.9 ? (1 - u) / 0.1 : 1;
  if (l.size === 0) { ctx.drawImage(spr, Math.round(x), Math.round(y)); ctx.globalAlpha = 1; return; }
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y));
  ctx.rotate(sw * 0.9 + 0.3 * wave(t, l.kr, l.p3));
  ctx.scale(1, Math.max(0.25, Math.abs(fl)));
  ctx.drawImage(spr, -(w >> 1), -(h >> 1));
  ctx.restore();
  ctx.globalAlpha = 1;
}
// leaves lying on the landing: still between gusts, each gust rolls them a third of the way across (they wrap, so
// three gusts bring every leaf back to where it lay at the start of the loop)
const GROUND_LEAVES = [];
{ const r = rng(77); for (let i = 0; i < 18; i++) GROUND_LEAVES.push({ id: i, x: r() * (W + 40), y: LAND + 6 + Math.round(Math.pow(r(), 0.8) * 54), m: r() < 0.3 ? 2 : 1, col: Math.floor(r() * 4), ph: r() }); }
const gustRun = t => { let d = 0; for (const g of GUSTS) d += smooth(g + 1, g + GUST_LEN - 1, t); return d / GUSTS.length; };
function drawGroundLeaves(t, g) {
  const run = gustRun(t);
  for (const l of GROUND_LEAVES) {
    const span = W + 40, x = ((l.x + run * span * l.m) % span) - 20;
    const hop = g > 0.05 ? Math.round(Math.abs(wave(t, 300 + l.id * 7, l.ph)) * g * (l.y > 240 ? 4 : 2)) : 0;
    const spr = LEAF_SPR[l.col][l.y > 248 ? 1 : 0][g > 0.05 && wave(t, 200 + l.id * 5, l.ph) < 0 ? 1 : 0];
    ctx.drawImage(spr, Math.round(x), l.y - hop - spr.height);
  }
}

/* ================= The temple's bell tower behind the wall: the bell, its striking log, the monk who swings it ================= */
const SHORO = { cx: 388, eave: 114, hw: 40, ridge: 99, rw: 17, post: 25 };
const BELL_B = { x: 381, y: 124, w: 14, h: 22 };
function renderShoro() {
  const p = pix(W, H), { cx, eave, hw, ridge, rw, post } = SHORO;
  for (const sx of [-1, 1]) p.fill(cx + sx * post - (sx > 0 ? 1 : 0), eave + 3, 2, 60, sx < 0 ? '#3a2018' : '#5a3020');   // posts
  p.fill(cx - post, eave + 4, post * 2, 2, '#4a2a1c');                                                                       // the tie beam
  for (let x = cx - hw; x <= cx + hw; x++) {                                                                                   // hipped roof, swept up at the corners
    const a = Math.abs(x - cx), up = Math.round(3 * Math.pow(clamp((a - (hw - 8)) / 8, 0, 1), 2));
    const top = a <= rw ? ridge : Math.round(ridge + (eave - 2 - ridge) * (1 - Math.pow(1 - (a - rw) / (hw - rw), 2)));
    for (let y = top - up; y <= eave - up; y++) p.set(x, y, y === top - up ? '#4a5272' : y >= eave - up - 1 ? ((x & 1) ? '#8a6448' : '#5a4440') : (x & 1) ? '#1c1e2c' : '#24283a');
    if (a <= hw - 4) { p.set(x, eave + 1, '#140e10'); p.set(x, eave + 2, (x >> 1) & 1 ? '#6a3a22' : '#2a1812'); }
  }
  for (let y = ridge - 3; y < ridge; y++) for (let x = cx - rw - 3; x <= cx + rw + 3; x++) p.set(x, y, y === ridge - 3 ? '#3a4262' : '#1a1c28');   // the ridge
  p.fill(cx - rw - 4, ridge - 5, 2, 3, '#2a2c3c'); p.fill(cx + rw + 3, ridge - 5, 2, 3, '#2a2c3c');                          // its end tiles
  p.fill(cx - 1, eave + 6, 2, BELL_B.y - eave - 6, '#2a2018');                                                               // the bell's hook
  return trim(p);
}
function renderBell() {
  const p = pix(BELL_B.w, BELL_B.h), { w, h } = BELL_B;
  for (let y = 0; y < h; y++) {
    const hw = y < 2 ? w / 2 - 2 : y > h - 3 ? w / 2 : w / 2 - 1;
    for (let x = 0; x < w; x++) {
      const dx = (x + 0.5 - w / 2) / hw;
      if (Math.abs(dx) > 1) continue;
      let col = pick(['#1a1810', '#2e2a1a', '#4a4026', '#7a6a3a', '#b89a58'], 0.25 + dx * 0.45 + (y > h - 4 ? 0.1 : 0), x, y);
      if (y === 7 || y === h - 5) col = '#5a4c2c';
      if (y > 1 && y < 6 && (x + (y & 1)) % 3 === 0) col = '#8a7444';                       // the rows of knobs
      p.set(x, y, col);
    }
  }
  p.set(w - 5, h - 8, '#c8a868'); p.set(w - 5, h - 9, '#8a7444');                            // the striking seat
  return p.done();
}
/** how far the log is drawn back from the bell at t: pulled back, let go, struck, swinging gently to rest */
function logBack(t) {
  for (const b of BELLS) {
    const d = t - b;
    if (d >= -3.4 && d < -0.35) return 7 * smooth(-3.4, -0.6, d);
    if (d >= -0.35 && d < 0) return 7 * (1 - (d + 0.35) / 0.35) - 1;
    if (d >= 0 && d < 5) return 3 * Math.exp(-d * 0.9) * Math.sin(Math.PI * d / 1.2);
  }
  return 0;
}
const ring = t => { for (const b of BELLS) if (t >= b && t < b + 9) return Math.exp(-(t - b) * 0.45); return 0; };
function drawShoro(t, crowd) {
  blit(SHORO_C);
  const rg = ring(t), shake = rg > 0.3 ? Math.round(wave(t, 2400) * rg) : 0;
  ctx.drawImage(BELL_C, BELL_B.x + shake, BELL_B.y);
  halo(HL.bell, BELL_B.x + 9, BELL_B.y + 12, 0.5 + rg * 0.6);
  const back = logBack(t), lx = BELL_B.x + BELL_B.w + 1 + Math.round(back);
  ctx.fillStyle = '#b8a070'; ctx.fillRect(lx + 5, SHORO.eave + 6, 1, 25 - 0); ctx.fillRect(lx + 17, SHORO.eave + 6, 1, 25);   // its two ropes
  rect(ctx, lx, BELL_B.y + 13, 22, 3, '#6a5034'); rect(ctx, lx, BELL_B.y + 13, 22, 1, '#9a7a4c'); rect(ctx, lx, BELL_B.y + 13, 1, 3, '#c8a868');   // the log
  if (crowd > 0) {                                     // the monk at the pull rope: shaven head, dark robe, an ochre kesa
    const mx = 428, ty = fig(mx, 168, 21, LK.monk, 'side', -1, 1, 'none');
    const hx = mx - 3 + Math.round(back * 0.6);
    ctx.fillStyle = '#b8a070'; for (let j = 0; j < 6; j++) ctx.fillRect(Math.round(lerp(lx + 21, hx, j / 5)), Math.round(lerp(BELL_B.y + 15, ty + 5, j / 5)), 1, 1);
    rect(ctx, hx, ty + 3, mx - hx, 1, LK.monk.sleeve); rect(ctx, hx, ty + 4, 1, 2, LK.monk.skin);
  }
}

/* ================= A paper street lamp (andon) on its post at the lane's foot ================= */
const ANDON = { x: 160, top: 172, base: 209 };
function renderAndon() {
  const p = pix(W, H), { x, top, base } = ANDON;
  p.fill(x - 1, top + 13, 3, base - top - 13, '#2a1a12'); p.fill(x - 1, top + 13, 1, base - top - 13, '#4a3020');
  p.fill(x - 5, top - 2, 11, 2, '#1e1214'); p.fill(x - 4, top - 3, 9, 1, '#3a4262');                           // its cap
  p.fill(x - 4, top, 9, 12, '#2a1a12');
  for (let y = top + 1; y < top + 11; y++) for (let i = x - 3; i <= x + 3; i++) p.set(i, y, pick(['#e0a050', '#f8d890', '#fff4d0'], 1 - Math.abs(i - x) / 4.5, i, y));
  glyphP(p, G5[1], x - 2, top + 2, '#3a1a10'); p.fill(x - 2, top + 8, 5, 1, '#3a1a10');
  return trim(p);
}

/* ================= People ================= */
// One function draws everyone, at any height (fig), from a look: kimono and haori, the geiko's black hem and white
// nape, the maiko's long sleeves and hanging obi, coats and scarves for the late-November visitors, the shopkeeper's
// samue and headscarf, the rickshaw man's black happi. Views: from the side (a four-frame walk, facing either way),
// from behind, from the front, and seated. The shoulders and hair catch a line of lantern light (topL).
//
// The crowd knob (CROWD, render(t, { crowd })): everyone who is not part of the story has a fixed rank in (0, 1) and is
// drawn only while rank < crowd, with what is theirs (tea bowl, dango, camera, phone glow, bag). The essential cast has
// rank 0: the tea house girl and the man behind the counter, the shopkeeper, the geiko, the couple in kimono, the two
// maiko, the rickshaw man and his passengers. At crowd 0 nobody is drawn: the rickshaw stays away, the tray and the
// bench stand empty, the counter is unattended and its window still lit; the cat and the heron stay.
const SKIN = ['#f0c8a0', '#e0b088', '#c88c62', '#f6f0ea'], HAIR = ['#141018', '#2a1c16', '#4a3020', '#8a8a92', '#a87040'];
function look(o) {
  const L = { ...o };
  L.skin = SKIN[o.sk ?? 0]; L.skinD = css(sh(L.skin, 0.8)); L.hair = o.bald ? css(sh(L.skin, 0.85)) : HAIR[o.hr ?? 0];
  L.top = o.top; L.topD = css(sh(o.top, 0.68)); L.topL = css(mixc(o.top, '#ffc880', 0.35));
  L.bot = o.bot || o.top; L.botD = css(sh(L.bot, 0.7));
  L.sleeve = css(sh(o.top, 0.84)); L.shoe = o.shoe || '#16121a';
  return L;
}
const LK = {
  geiko: look({ top: '#16141c', robe: 1, obi: '#c8a050', hem: '#a8282a', sk: 3, hr: 0, geiko: 1, collar: '#d83a3a', kan: '#f0c860', tabi: 1 }),
  maikoA: look({ top: '#b8283a', robe: 1, obi: '#2a2440', hem: '#f0c060', sk: 3, hr: 0, geiko: 1, maiko: 1, collar: '#e83a3a', kan: '#ff8a3a', pat: '#f6d080', tabi: 1 }),
  maikoB: look({ top: '#1f6a6a', robe: 1, obi: '#d8a040', hem: '#f0e0c0', sk: 3, hr: 0, geiko: 1, maiko: 1, collar: '#e83a3a', kan: '#e8483a', pat: '#f0a0b0', tabi: 1 }),
  her: look({ top: '#c86a9a', robe: 1, obi: '#f0d080', pat: '#f8d8e8', sk: 0, hr: 1, bun: '#f0f0f0', tabi: 1 }),
  him: look({ top: '#3a3a4a', robe: 1, man: 1, obi: '#7a6a5a', haori: '#22222c', sk: 1, hr: 0 }),
  server: look({ top: '#7a3a4a', robe: 1, obi: '#e8d8b0', apron: '#e8e0d0', sk: 0, hr: 0, bun: '#2a1c16', tabi: 1 }),
  master: look({ top: '#2a3450', sk: 1, hr: 3, scarf: '#e8e4dc' }),
  sweeper: look({ top: '#2a3a5a', bot: '#2a3a5a', sk: 1, hr: 3, scarf: '#d8d4cc', apron: '#5a4a3a' }),
  puller: look({ top: '#16161e', bot: '#16161e', sk: 2, hr: 0, band: '#e8e4dc', shoe: '#0c0c10' }),
  coatA: look({ top: '#6a4a3a', bot: '#24242e', sk: 0, hr: 1, scarf: '#c83a3a', long: 1 }),
  coatB: look({ top: '#2a3a4a', bot: '#3a3a44', sk: 1, hr: 0, cap: '#3a3a44' }),
  coatC: look({ top: '#8a7a5a', bot: '#2a2a34', sk: 0, hr: 2, long: 1, bag: '#3a2a22' }),
  coatD: look({ top: '#3a4a3a', bot: '#2a2a30', sk: 2, hr: 0, pack: '#5a3a3a' }),
  coatE: look({ top: '#c8c0b0', bot: '#3a3446', sk: 0, hr: 0, scarf: '#3a5a8a', long: 1 }),
  coatF: look({ top: '#4a2a3a', bot: '#1e1e26', sk: 1, hr: 3, cap: '#6a5a4a', cane: 1 }),
  kimonoG: look({ top: '#5a6a4a', robe: 1, obi: '#c8b080', shawl: '#e8dcc8', sk: 0, hr: 3, bun: '#8a8a92', tabi: 1 }),
  coatH: look({ top: '#1e2a40', bot: '#2a2a30', sk: 0, hr: 1, scarf: '#e8a030', bag: '#a83a2a' }),
  guestA: look({ top: '#5a3a5a', bot: '#2a2a3a', sk: 0, hr: 1, long: 1, scarf: '#e8d8b8' }),
  guestB: look({ top: '#3a4a5a', bot: '#2a2a30', sk: 1, hr: 0 }),
  pass1: look({ top: '#a83a4a', sk: 0, hr: 1, long: 1, scarf: '#e8e0d0' }), pass2: look({ top: '#3a4a6a', sk: 0, hr: 0 }),
  monk: look({ top: '#24222c', robe: 1, man: 1, obi: '#8a6a3a', shawl: '#8a6a3a', sk: 1, bald: 1 }),
  counter: look({ top: '#4a5a4a', bot: '#2a2a32', sk: 1, hr: 2, cap: '#8a3a2a' }),
};
const FS = h => {                                      // head, torso and leg rows, body and head widths for a height
  const hh = h < 16 ? 3 : h < 20 ? 4 : 5, th = Math.round(h * 0.37);
  return { hh, th, lh: h - hh - th, bw: h < 20 ? 4 : 5, hw: h < 16 ? 3 : 4 };
};
/** a person, feet on row fy, h rows tall, centred on column x. view 'side' (dir 1 faces right, -1 left; frames 0..3),
 *  'back', 'front', 'sit' (front, seated), 'sitBack'. arm: 'swing' (default), 'down', 'none'. Returns the shoulder row. */
function fig(x, fy, h, L, view = 'side', dir = 1, f = 0, arm = 'swing') {
  const top = fy - h + 1;
  if (h < 14) return mini(x, fy, h, L, f);
  const { hh, th, lh, bw, hw } = FS(h), ty = top + hh, ly = ty + th;
  const R = (dx, dy, w, hgt, col) => { if (w > 0 && hgt > 0) rect(ctx, dir > 0 ? x + dx : x - dx - w + 1, top + dy, w, hgt, col); };
  const side = view === 'side', robe = L.robe, sit = view === 'sit' || view === 'sitBack';
  const swing = side ? (f === 0 ? 1 : f === 2 ? -1 : 0) : 0;
  if (side) {
    if (robe) {                                        // a kimono to the ankles, the hem kicked by short steps
      const n = h - hh - 2;
      R(-2, hh, bw, n, L.top); R(-2, hh, 1, n, L.topD);
      if (L.hem) R(-2, h - 6, bw, 4, L.hem);
      if (L.pat) for (let j = hh + 2; j < h - 2; j += 3) R(((j * 7) % 3) - 1, j, 1, 1, L.pat);
      if (L.haori) R(-2, hh, bw, th + 2, L.haori);
      R(-2, hh + Math.round(th * 0.6), bw, L.man ? 1 : 2, L.obi);
      if (!L.man) R(-3, hh + Math.round(th * 0.45), 1, 3, L.obi);                                 // the bow at the back
      if (L.maiko) R(-3, hh + th, 1, Math.round(lh * 0.75), L.obi);                              // the darari, hanging long
      const st = swing;
      R(-1 - st, h - 2, 2, 1, L.tabi ? '#f0ece4' : L.skinD); R(st, h - 2, 2, 1, L.tabi ? '#e8e4dc' : L.skin);
      R(-2 - st, h - 1, 3, 1, L.maiko ? '#3a2418' : '#2a1a14'); R(st - 1, h - 1, 3, 1, L.maiko ? '#4a2a1a' : '#3a241a');
      if (L.maiko) R(-1, h - 1, 2, 1, '#2a1810');
    } else {
      for (const far of [true, false]) {
        const d = far ? -swing : swing, lifted = swing === 0 && far === (f === 1);
        for (let r = 0; r < lh; r++) {
          const s = Math.round(d * 2.2 * r / (lh - 1)), xx = (far ? -1 : 0) + s - (lifted && r >= lh - 3 ? 1 : 0);
          if (lifted && r === lh - 1) continue;
          const shoe = r === lh - 1 || (lifted && r === lh - 2);
          R(xx, hh + th + r, shoe ? 3 : 2, 1, shoe ? L.shoe : far ? L.botD : L.bot);
        }
      }
      R(-2, hh, bw, th, L.top); R(-2, hh, 1, th, L.topD);
      if (L.apron) R(0, hh + 2, bw - 2, th + 2, L.apron);
      if (L.pack) { R(-4, hh + 1, 2, th - 3, L.pack); R(-4, hh + 1, 2, 1, css(sh(L.pack, 1.3))); }
    }
    R(-1, hh, bw - 1, 1, L.topL);
    if (L.scarf) R(-2, hh, bw, 2, L.scarf);
    if (L.shawl) R(-2, hh, bw, 3, L.shawl);
    if (L.collar) R(0, hh, 2, 1, L.collar);
    if (arm === 'swing') {
      const fx = swing, wide = robe ? 2 : 1;
      R(0, hh + 1, wide, robe ? 4 : 2, L.sleeve); R(fx, hh + 3, 1, th - 4, robe ? L.sleeve : L.skinD); R(fx, hh + th - 1, 1, 1, L.skin);
      if (L.maiko) R(0, hh + 2, 2, th + 1, L.sleeve);
      if (L.bag) { R(fx - 1, hh + th, 3, 3, L.bag); }
      if (L.cane) { for (let j = 0; j < lh; j++) R(fx + 2 + (j > lh / 2 ? 0 : 0), hh + th + j - 1, 1, 1, '#6a5a3a'); }
    } else if (arm === 'down') R(0, hh + 1, 1, th - 1, robe ? L.sleeve : L.top);
    // the head
    R(-2, 0, hw, hh - 1, L.hair); R(-1, 1, hw - 1, hh - 2, L.skin); R(-1, hh - 1, 2, 1, L.skinD);
    rect(ctx, dir > 0 ? x + hw - 3 : x - hw + 3, top + 2, 1, 1, L.geiko ? '#a01818' : '#2a1a14');
    if (L.geiko) { R(-3, -2, hw + 1, 3, L.hair); R(-3, 0, 1, hh - 1, L.hair); R(-4, 1, 1, 2, L.hair); R(-1, -2, 1, 1, L.kan); R(1, -1, 1, 1, L.kan); if (L.maiko) { R(1, -2, 2, 2, L.kan); R(2, 0, 1, 2, L.kan); } }
    else if (L.long) { R(-3, 1, 1, hh + 1, L.hair); R(-2, hh - 1, 1, 2, L.hair); }
    if (L.bun) { R(-3, -1, 2, 2, L.hair); R(-3, -1, 1, 1, L.bun); }
    if (L.cap) R(-2, 0, hw + 1, 1, L.cap);
    if (L.band) R(-2, 1, hw, 1, L.band);
    if (L.scarf && L.apron) R(-2, 0, hw, 1, L.scarf);
    return ty;
  }
  // back, front and seated views: a wider body, both legs
  const front = view === 'front' || view === 'sit', W2 = bw + 1, x0 = -(W2 >> 1);
  if (sit) {
    const sl = Math.max(4, Math.round(lh * 0.55));
    R(x0, hh + th, W2, 2, robe ? L.top : L.bot);
    for (const lx of [x0, x0 + W2 - 2]) { R(lx, hh + th + 2, 2, sl - 3, robe ? L.top : L.botD); R(lx, hh + th + sl - 1, 2, 1, robe && L.tabi ? '#e8e4dc' : L.shoe); }
  } else if (robe) {
    R(x0, hh, W2, h - hh - 2, L.top); R(x0, hh, 1, h - hh - 2, L.topD); R(x0 + W2 - 1, hh, 1, h - hh - 2, L.topD);
    if (L.hem) R(x0, h - 6, W2, 4, L.hem);
    R(x0, h - 2, 2, 1, L.tabi ? '#f0ece4' : L.skin); R(x0 + W2 - 2, h - 2, 2, 1, L.tabi ? '#f0ece4' : L.skin);
    R(x0, h - 1, 2, 1, '#2a1a14'); R(x0 + W2 - 2, h - 1, 2, 1, '#2a1a14');
  } else for (const lx of [x0, x0 + W2 - 2]) { for (let r = 0; r < lh; r++) R(lx, hh + th + r, 2, 1, r === lh - 1 ? L.shoe : L.bot); R(lx + (lx === x0 ? 1 : 0), hh + th, 1, lh - 1, L.botD); }
  R(x0, hh, W2, th, L.top); R(x0, hh, 1, th, L.topD); R(x0 + W2 - 1, hh, 1, th, L.topD); R(x0 + 1, hh, W2 - 2, 1, L.topL);
  if (L.haori) R(x0, hh, W2, th + 2, L.haori);
  if (robe) { R(x0, hh + Math.round(th * 0.6), W2, L.man ? 1 : 2, L.obi); if (!front && !L.man) R(x0 + 1, hh + Math.round(th * 0.35), W2 - 2, 3, css(sh(L.obi, 1.15))); if (!front && L.maiko) R(x0 + 1, hh + th, W2 - 2, Math.round(lh * 0.8), L.obi); }
  if (L.apron && front) R(x0 + 1, hh + 2, W2 - 2, th + (robe ? 6 : 2), L.apron);
  if (L.pack && !front) { R(x0 + 1, hh + 1, W2 - 2, th - 2, L.pack); R(x0 + 1, hh + 1, W2 - 2, 1, css(sh(L.pack, 1.3))); }
  if (L.scarf) R(x0, hh, W2, 2, L.scarf);
  if (L.shawl) R(x0, hh, W2, 3, L.shawl);
  if (front && L.collar) { R(-1, hh, 1, 2, L.collar); R(0, hh, 1, 2, '#f0ece4'); }
  if (arm !== 'none') for (const ax of [x0 - 1, x0 + W2]) { R(ax, hh, 1, robe ? 5 : 2, L.sleeve); R(ax, hh + (robe ? 5 : 2), 1, th - (robe ? 6 : 3), robe ? L.sleeve : L.skinD); R(ax, hh + th - 1, 1, 1, L.skin); }
  R(-2, 1, 4, hh - 1, front ? L.skin : L.hair); R(-2, 0, 4, 1, L.hair);
  if (front) { R(-2, 1, 1, hh - 3, L.hair); R(1, 1, 1, hh - 3, L.hair); R(-1, hh - 2, 1, 1, '#2a1a14'); R(1, hh - 2, 1, 1, '#2a1a14'); }
  if (L.geiko) { R(-3, -2, 6, 3, L.hair); R(-3, 0, 1, 2, L.hair); R(2, 0, 1, 2, L.hair); R(-2, -2, 1, 1, L.kan); R(2, -1, 1, 1, L.kan); if (!front) R(-1, hh - 1, 2, 2, '#f6f0ea'); }
  else if (L.long) { R(-3, 1, 1, hh + 1, L.hair); R(2, 1, 1, hh + 1, L.hair); if (!front) R(-2, hh - 1, 4, 2, L.hair); }
  if (L.bun) { R(-1, -1, 2, 1, L.hair); R(1, -1, 1, 1, L.bun); }
  if (L.cap) R(-2, 0, 4, front ? 1 : 2, L.cap);
  if (L.band) R(-2, 1, 4, 1, L.band);
  return ty;
}
/** a figure under 14 rows: head, body, legs that alternate */
function mini(x, fy, h, L, f) {
  const top = fy - h + 1, hh = Math.max(1, Math.round(h * 0.22)), bh = Math.max(1, Math.round(h * (L.robe ? 0.7 : 0.4))), w = h < 7 ? 1 : 2;
  rect(ctx, x, top, w, hh, L.geiko ? L.skin : L.hair);
  if (h >= 7) rect(ctx, x, top, w, 1, L.hair);
  rect(ctx, x, top + hh, w, bh, L.top);
  if (h >= 9) rect(ctx, x, top + hh, w, 1, L.topL);
  const lr = h - hh - bh;
  if (lr > 0) { if (L.robe || w < 2) rect(ctx, x, top + hh + bh, w, lr, L.robe ? L.top : L.bot); else { rect(ctx, x + (f & 1), top + hh + bh, 1, lr, L.bot); rect(ctx, x + 1 - (f & 1), top + hh + bh, 1, lr - 1, L.botD); } }
  return top + hh;
}

/* ---- routes: in along the landing's back row and up the lane, or down the lane and off along the row ---- */
const BACK_ROW = 212, H_NEAR = 22;
const MOUTH_Z = 1, TOP_Z = 8.3;
/** where a route walker is at time t: { x, fy, h, dir, view } or null. r = { a, turn, b, edge (-1 left, 1 right), up, lat } */
function routeAt(r, t) {
  if (t < r.a || t >= r.b) return null;
  const mouthX = VP.x + r.lat * 70, edgeX = r.edge < 0 ? -14 : W + 14;
  const along = up => { const u = up ? (t - r.turn) / (r.b - r.turn) : (t - r.a) / (r.turn - r.a); return lerp(MOUTH_Z, TOP_Z, up ? u : 1 - u); };
  if (r.up ? t < r.turn : t >= r.turn) {             // on the landing's back row
    const u = r.up ? (t - r.a) / (r.turn - r.a) : (t - r.turn) / (r.b - r.turn);
    const x = r.up ? lerp(edgeX, mouthX, u) : lerp(mouthX, edgeX, u), climb = smooth(0.78, 1, r.up ? u : 1 - u);   // up the step into the lane
    return { x: Math.round(x), fy: Math.round(lerp(BACK_ROW, gY(1), climb)), h: H_NEAR, dir: r.up ? -r.edge : r.edge, view: 'side' };
  }
  let z = along(r.up);
  if (r.keys) for (let k = 1; k < r.keys.length; k++) if (t < r.keys[k][0]) { const [t0, z0] = r.keys[k - 1], [t1, z1] = r.keys[k]; z = lerp(z0, z1, (t - t0) / (t1 - t0)); break; }
  const s = 1 / z;
  return { x: Math.round(VP.x + r.lat * 70 * s), fy: Math.round(gY(s)), h: Math.max(2, Math.round(H_NEAR * s)), dir: 1, view: r.up ? 'back' : 'front', s };
}

/* ---- the cast's routes and the walkers ---- */
const GEIKO_RT = { a: GEIKO.a, turn: GEIKO.turn, b: GEIKO.b, edge: -1, up: true, lat: -0.12 };
const COUPLE_RT = [0, 0.13].map(dl => ({ a: COUPLE.a + dl * 6, turn: COUPLE.turn, b: COUPLE.b, edge: 1, up: true, lat: 0.02 + dl,
  keys: [[COUPLE.turn, 1], [COUPLE.stop, 1.35], [COUPLE.go, 1.35], [COUPLE.b, TOP_Z]] }));
const MAIKO_RT = [0, 0.14].map((dl, i) => ({ a: MAIKO.a + i * 1.2, turn: MAIKO.foot + i * 1.2, b: MAIKO.b + i * 1.2, edge: 1, up: false, lat: -0.06 + dl }));
// visitors up and down the lane: [look, period, a, turn, b, edge, up, lat]; pairs share a rank
const LANE_RT = [
  ['coatE', 80, 2, 11, 52, 1, true, 0.26], ['coatB', 60, 4, 44, 53, -1, false, -0.22], ['coatC', 120, 60, 70, 116, -1, true, -0.34],
  ['coatH', 80, 28, 70, 78, 1, false, 0.36], ['guestA', 120, 4, 48, 58, -1, false, 0.04], ['guestB', 120, 4.6, 48.6, 58.6, -1, false, 0.16, 1],
  ['coatD', 240, 150, 160, 206, 1, true, 0.3],
].map(([lk, P, a, turn, b, edge, up, lat, pair], i) => ({ L: LK[lk], P, a, turn, b, edge, up, lat, pair, id: 20 + i }));
{ let unit = -1; LANE_RT.forEach(w => { if (!w.pair) unit++; w.rank = gold(unit + 3); }); }
// visitors crossing the landing: [look, feet row, crossings per loop, phase, direction, steps a second]
const WK = [
  ['coatD', 222, 3, 0.62, -1, 4], ['coatF', 228, 2, 0.15, -1, 2.5], ['kimonoG', 236, 2, 0.4, 1, 2.5], ['coatE', 242, 3, 0.85, 1, 4],
  ['coatA', 248, 3, 0.3, 1, 4], ['coatB', 249, 3, 0.316, 1, 4, 1], ['coatC', 258, 4, 0.05, -1, 4.5], ['coatH', 264, 4, 0.55, 1, 4.5],
].map(([lk, fy, k, ph, dir, fps, pair], i) => ({ L: LK[lk], fy, k, ph, dir, fps, pair, id: 40 + i, h: fy < 230 ? 21 : fy < 250 ? 23 : 25 }));
{ let unit = -1; WK.forEach(w => { if (!w.pair) unit++; w.rank = gold(unit + 11); }); }
const RANK = { counter: 0.04, bench: 0.27 };
const walkFrame = (t, fps, id) => Math.floor(t * fps + id * 1.3) & 3;

/* ---- the geiko and her lantern; the couple's photo; the maiko ---- */
function drawRouteWalker(r, L, t, id, fps = 3.2, extra) {
  const p = routeAt(r, t);
  if (!p) return null;
  const f = walkFrame(t, fps, id);
  const ty = fig(p.x, p.fy - (p.view !== 'side' && p.h >= 14 && (f & 1) ? 1 : 0), p.h, L, p.view, p.dir, f);
  if (extra) extra(p, ty, f);
  return p;
}
function geikoLantern(p, ty, f) {                    // a small paper lantern carried at her side, swaying with her steps
  const sc = p.h / H_NEAR, lx = p.x + Math.round((p.view === 'side' ? p.dir * 4 : 4) * sc), ly = ty + Math.round(9 * sc) + (f & 1);
  const spr = CHO_W[clamp(Math.round(6 * sc), 2, 10)];
  ctx.drawImage(spr, lx - (spr.width >> 1), ly);
  halo(sc > 0.5 ? HL.lantern : HL.lanternS, lx, ly + 3, 0.8);
  drawPool(sc > 0.6 ? POOL.laneM : POOL.laneS, lx, p.fy, 0.5);
}
function couplePhoto(p, ty, f, t) {                   // she holds her phone up to the pagoda; its screen lights her hands
  if (t < COUPLE.stop + 0.6 || t > COUPLE.go - 0.4) return;
  rect(ctx, p.x + 2, ty - 6, 1, 6, LK.her.sleeve); rect(ctx, p.x + 1, ty - 9, 3, 4, '#1a1a22'); rect(ctx, p.x + 2, ty - 8, 1, 2, '#bfe4ff');
  halo(HL.phone, p.x + 2, ty - 7, 0.7);
}

/* ---- the tea house: the man at the counter and his guest, the bench guests, the girl who serves them ---- */
function sitFig(x, seat, h, L, view, arm) { const { hh, th } = FS(h), top = seat - hh - th + 1; return fig(x, top + h - 1, h, L, view, 1, 0, arm); }
function drawCounter(t, crowd) {
  if (crowd <= 0) return;
  // behind the window: the master whisks a bowl, his arm going and coming
  const mty = fig(24, 196, 21, LK.master, 'front', 1, 0, 'none');
  const wk = Math.round(1.5 * wave(t, 480));
  rect(ctx, 20, mty, 1, 6, LK.master.sleeve); rect(ctx, 28, mty, 1, 3, LK.master.sleeve); rect(ctx, 27 + wk, mty + 3, 2, 2, LK.master.skin);
  rect(ctx, 25, mty + 6, 5, 2, '#2a2a22'); rect(ctx, 26, mty + 6, 3, 1, '#8ab858');
  rect(ctx, TEA_COUNTER.x0 - 1, TEA_COUNTER.sill, TEA_COUNTER.x1 - TEA_COUNTER.x0 + 3, 1, '#b8844e'); rect(ctx, TEA_COUNTER.x0 - 1, TEA_COUNTER.sill + 1, TEA_COUNTER.x1 - TEA_COUNTER.x0 + 3, 2, '#5a3a22');
  for (let k = 0; k < 3; k++) {                         // steam off the kettle at the window's corner
    const fr = frac(t / 3 + k / 3), sx = 8 + Math.round(Math.sin(TAU * (fr + k * 0.3)) * 1.5), sy = 184 - Math.round(fr * 14);
    ctx.globalAlpha = 0.5 * (1 - fr); rect(ctx, sx, sy, 2, 1, '#f0e8e0'); ctx.globalAlpha = 1;
  }
  if (RANK.counter < crowd) {                           // his guest on a stool, sipping now and then
    rect(ctx, 11, 198, 9, 1, '#6a4a2e'); rect(ctx, 12, 199, 1, 8, '#3a2418'); rect(ctx, 18, 199, 1, 8, '#3a2418');
    const ty = sitFig(15, 197, 21, LK.counter, 'sitBack', 'none');
    const u = frac(t / 14 + 0.3), up = u < 0.1 ? u / 0.1 : u < 0.28 ? 1 : u < 0.38 ? 1 - (u - 0.28) / 0.1 : 0;
    rect(ctx, 11, ty, 1, 7, LK.counter.sleeve);
    rect(ctx, 19, ty, 1, Math.round(7 - up * 4), LK.counter.sleeve);
    rect(ctx, 18 + Math.round(up), ty + Math.round(7 - up * 6), 2, 2, '#e8dcc0');
  }
}
function drawBenchGuests(t, crowd) {
  if (!(RANK.bench < crowd)) return;
  const A = BENCH.x0 + 11, B = BENCH.x0 + 28;
  rect(ctx, A + 6, BENCH.seat - 3, 9, 2, '#3a2418'); rect(ctx, A + 7, BENCH.seat - 4, 2, 1, '#2a3a28'); rect(ctx, A + 12, BENCH.seat - 4, 2, 1, '#e8dcc0');   // the tray between them
  // she lifts her matcha bowl in both hands
  const tA = sitFig(A, BENCH.seat - 1, 21, LK.guestA, 'sit', 'none'), uA = frac(t / 12), upA = uA < 0.12 ? uA / 0.12 : uA < 0.36 ? 1 : uA < 0.46 ? 1 - (uA - 0.36) / 0.1 : 0;
  const by = Math.round(lerp(tA + 8, tA - 1, upA));
  rect(ctx, A - 4, tA, 1, Math.max(2, by - tA + 1), LK.guestA.sleeve); rect(ctx, A + 3, tA, 1, Math.max(2, by - tA + 1), LK.guestA.sleeve);
  rect(ctx, A - 3, by, 6, 2, '#2a3a2a'); rect(ctx, A - 2, by, 4, 1, '#9ac860');
  // he eats dango off a skewer
  const tB = sitFig(B, BENCH.seat - 1, 22, LK.guestB, 'sit', 'none'), uB = frac(t / 10 + 0.4), upB = uB < 0.15 ? uB / 0.15 : uB < 0.3 ? 1 : uB < 0.42 ? 1 - (uB - 0.3) / 0.12 : 0;
  rect(ctx, B - 4, tB, 1, 7, LK.guestB.sleeve);
  const hx = Math.round(lerp(B + 4, B + 1, upB)), hy = Math.round(lerp(tB + 7, tB - 1, upB));
  rect(ctx, B + 4, tB, 1, 3, LK.guestB.sleeve); rect(ctx, hx, hy, 1, 2, LK.guestB.skin);
  rect(ctx, hx, hy - 6, 1, 6, '#d8c090'); rect(ctx, hx - 1, hy - 6, 3, 2, '#8a4a1e'); rect(ctx, hx - 1, hy - 4, 3, 2, '#a8602a');
}
function serveAt(t) { const c = ((t - 20) % SERVE + SERVE) % SERVE; return c < 14 ? c : -1; }
function drawServer(t, crowd) {
  const c = serveAt(t);
  if (c < 0 || crowd <= 0) return;
  let x, fy, view = 'side', dir = 1, bow = 0;
  if (c < 1.5) { x = 55; fy = 206; view = 'front'; }
  else if (c < 5) { x = Math.round(lerp(55, 94, (c - 1.5) / 3.5)); fy = Math.round(lerp(206, 210, (c - 1.5) / 3.5)); }
  else if (c < 9) { x = 94; fy = 210; view = 'front'; bow = c > 6 && c < 8 ? 1 : 0; }
  else if (c < 13) { x = Math.round(lerp(94, 55, (c - 9) / 4)); fy = Math.round(lerp(210, 206, (c - 9) / 4)); dir = -1; }
  else { x = 55; fy = 206; view = 'back'; }
  const ty = fig(x, fy + bow, 21 - bow, LK.server, view, dir, walkFrame(t, 3.2, 5), view === 'side' ? 'none' : 'swing');
  if (view === 'side') {                               // the tray held out in front
    rect(ctx, x + dir * 2 - (dir < 0 ? 1 : 0), ty + 4, 2, 1, LK.server.sleeve);
    rect(ctx, dir > 0 ? x + 3 : x - 9, ty + 4, 7, 1, '#3a2418'); rect(ctx, dir > 0 ? x + 4 : x - 8, ty + 3, 2, 1, '#2a3a28'); rect(ctx, dir > 0 ? x + 7 : x - 5, ty + 3, 1, 1, '#e8dcc0');
  }
}
/* the noren lifts while she passes through it */
const norenPush = t => { const c = serveAt(t); return c < 0 ? 0 : env(c, 0, 2, 0.6) + env(c, 12.4, 14, 0.6); };

/* ---- the shopkeeper sweeping leaves off the lane in front of her shop ---- */
function drawSweeper(t, g, crowd) {
  if (crowd <= 0) return;
  const z = 1.16 + 0.08 * wave(t, 4, 0.1), s = 1 / z, x = Math.round(sideX('R', s, 20)), fy = Math.round(gY(s)), h = Math.round(H_NEAR * s);
  rect(ctx, x + 8, fy - 2, 5, 2, '#2a2a30'); rect(ctx, x + 8, fy - 3, 1, 1, '#3a3a44');                    // the dustpan
  for (const [dx, col] of [[4, '#b8361e'], [5, '#d4542a'], [6, '#8a2a1e'], [5, '#e07a30']]) rect(ctx, x + dx, fy - (dx === 5 && col === '#e07a30' ? 1 : 0), 1, 1, col);   // her pile
  if (g > 0.25) { fig(x, fy, h, LK.sweeper, 'front', 1, 0, 'none'); const ty = fy - h + FS(h).hh + 1; rect(ctx, x - 3, ty - 4, 1, 4, LK.sweeper.sleeve); rect(ctx, x - 2, ty - 6, 1, 2, LK.sweeper.skin); rect(ctx, x + 3, ty, 1, 6, LK.sweeper.sleeve); rect(ctx, x + 4, ty - 2, 1, 12, '#8a7040'); return; }   // she holds her scarf against the gust
  const ty = fig(x, fy, h, LK.sweeper, 'side', -1, 1, 'none'), sw = Math.round(3 * wave(t, g > 0 || t % 90 < 30 ? 240 : 200));   // faster just after a gust
  const bx = x - 7 + sw;
  ctx.fillStyle = '#8a7040';
  for (let j = 0; j <= 10; j++) ctx.fillRect(Math.round(lerp(x - 1, bx + 1, j / 10)), Math.round(lerp(ty + 1, fy - 3, j / 10)), 1, 1);   // the bamboo handle
  rect(ctx, bx - 1, fy - 3, 4, 2, '#a88a4a'); rect(ctx, bx - 2, fy - 1, 6, 1, '#7a6030');                   // the broom head
  rect(ctx, x - 1, ty + 1, 1, 3, LK.sweeper.sleeve); rect(ctx, x - 2, ty + 4, 1, 1, LK.sweeper.skin);
  if (Math.abs(sw) === 3 && hash(7, step(t, 2400), 3) < 0.6) rect(ctx, bx + (sw > 0 ? 4 : -3), fy - 2 - Math.floor(hash(8, step(t, 2400)) * 3), 1, 1, '#d4542a');   // a flicked leaf
}

/* ---- the rickshaw: a black lacquered jinrikisha, two passengers under a red blanket, a paper lantern, the puller ---- */
function drawRickshaw(t, crowd) {
  if (crowd <= 0) return;
  const R = RICK.find(r => t >= r.a && t < r.b);
  if (!R) return;
  const u = (t - R.a) / (R.b - R.a), dir = R.dir, wx = Math.round(dir > 0 ? lerp(-60, 520, u) : lerp(540, -40, u)), fy = 252;
  const Q = (dx, dy, w, h, col) => rect(ctx, dir > 0 ? wx + dx : wx - dx - w + 1, fy + dy, w, h, col);
  // passengers, seated side by side under the folded hood
  for (const [dx, L] of [[-8, LK.pass1], [-3, LK.pass2]]) {
    Q(dx - 1, -33, 4, 4, L.hair); Q(dx, -32, 3, 3, L.skin); Q(dx - 1, -29, 5, 9, L.top); Q(dx - 1, -29, 5, 1, L.topL);
    if (L.scarf) Q(dx - 1, -29, 5, 2, L.scarf);
  }
  Q(-19, -37, 5, 2, '#141018'); Q(-20, -35, 4, 12, '#141018'); Q(-19, -37, 4, 1, '#5a5468'); Q(-20, -35, 1, 12, '#3a3446');   // the folded hood
  Q(-14, -24, 20, 9, '#1c1822'); Q(-14, -24, 20, 1, '#c8a050'); Q(-14, -16, 20, 1, '#8a7040'); Q(-14, -23, 1, 7, '#4a4458');   // the body, gold trim
  Q(-11, -22, 15, 5, '#b8281e'); Q(-11, -22, 15, 1, '#e8483a');                                            // the red blanket
  Q(4, -14, 6, 1, '#3a3446'); Q(8, -13, 1, 5, '#2a2430');                                                  // footboard
  ctx.fillStyle = '#8a6a4a';
  for (let j = 0; j <= 22; j++) { const X = 4 + j, Y = -15 + Math.round(j * 0.12); ctx.fillRect(dir > 0 ? wx + X : wx - X, fy + Y, 1, 1); }   // the shafts
  // the wheel turns with the distance run
  const rot = (wx * dir) / 10, cx = dir > 0 ? wx - 4 : wx + 4, cy = fy - 10;
  ctx.fillStyle = '#8a7a5a';
  for (let k = 0; k < 6; k++) { const a = rot + k * Math.PI / 3; for (let j = 2; j < 10; j++) ctx.fillRect(Math.round(cx + Math.cos(a) * j), Math.round(cy + Math.sin(a) * j), 1, 1); }
  for (let k = 0; k < 48; k++) {
    const a = k * TAU / 48, lit = k > 28 && k < 42;
    ctx.fillStyle = lit ? '#d8b880' : '#4a3e3a'; ctx.fillRect(Math.round(cx + Math.cos(a) * 10), Math.round(cy + Math.sin(a) * 10), 1, 1);
    ctx.fillStyle = '#16121a'; ctx.fillRect(Math.round(cx + Math.cos(a) * 11), Math.round(cy + Math.sin(a) * 11), 1, 1);
  }
  rect(ctx, cx - 1, cy - 1, 2, 2, '#c8a050');
  // its lantern, hung at the front corner
  const lx = dir > 0 ? wx + 6 : wx - 6;
  rect(ctx, lx, fy - 27, 1, 2, '#2a1a10'); ctx.drawImage(CHO_R[6], lx - 2, fy - 25); halo(HL.lantern, lx, fy - 22, 0.85);
  // the puller, running between the shafts, the crossbar in his hands
  const px = dir > 0 ? wx + 27 : wx - 27, ty = fig(px, fy, 24, LK.puller, 'side', dir, Math.floor(t * 6.4) & 3, 'none');
  rect(ctx, px - dir * 1, ty + 1, 1, 4, LK.puller.top); rect(ctx, px - dir * 2, ty + 5, 1, 3, LK.puller.skin);
}

/* ---- the cat on the wall's coping; the night heron on the pagoda ---- */
const CAT_SIT = ['o.o....', 'ooo....', 'wew....', '.wwk...', '.wwkk..', 'wwwkkw.', 'wwwwww.'];
const CAT_WALK_F = [['.......o.o', '.......oow', 'wwwwkkwwkw', 'wwkkwwww..', '.w..w..w..'], ['.......o.o', '.......oow', 'wwwwkkwwkw', 'wwkkwwww..', 'w..w..w.w.']];
const CAT_KEY = { o: '#c87a3a', w: '#d8d0c4', k: '#24202a', e: '#e8d050' };
function catSprite(rows, flip) { const p = pix(rows[0].length, rows.length); p.rows(rows, 0, 0, CAT_KEY, flip); return p.done(); }
function drawCat(t, g) {
  const feet = WALL.top - 8;
  const walk = CAT_WALK.find(([a, b]) => t >= a && t < b);
  let x = t >= CAT_WALK[0][1] && t < CAT_WALK[1][0] ? 444 : 404;
  if (walk) {
    const [a, b, dir] = walk, u = (t - a) / (b - a);
    x = Math.round(dir > 0 ? lerp(404, 444, u) : lerp(444, 404, u));
    ctx.drawImage(CAT_SPR.walk[Math.floor(t * 4) & 1][dir > 0 ? 0 : 1], x - 5, feet - 4);
    rect(ctx, dir > 0 ? x - 6 : x + 5, feet - 5 - (Math.floor(t * 2) & 1), 1, 2, '#c87a3a');
    return;
  }
  const facing = x > 420 ? 1 : 0;                     // she sits looking along the wall, towards the lane from the far end
  ctx.drawImage(CAT_SPR.sit[facing], facing ? x - 4 : x - 2, feet - 6);
  const sw = wave(t, 30, 0.2) + g;                    // the tail swishes along the tiles
  for (let k = 0; k < 4; k++) rect(ctx, (facing ? x - 5 : x + 5) + (facing ? -k : k), feet - Math.round(Math.max(0, sw) * (k * 0.8)), 1, 1, k === 3 ? '#24202a' : '#d8d0c4');
  if (BELLS.some(b => t > b && t < b + 6)) rect(ctx, facing ? x - 2 : x + 2, feet - 6, 1, 1, '#c87a3a');   // an ear turns to the bell
}
const PERCH = { x: PAG.cx + tipOf(ROOFS[1]).x - 1, y: tipOf(ROOFS[1]).y - 1 };
function heronAt(t) {
  const circ = (a) => ({ x: 240 + 50 * Math.cos(a), y: 52 + 16 * Math.sin(a) });
  if (t < HERON.off || t >= HERON.land) return { x: PERCH.x, y: PERCH.y, perch: true };
  if (t < 119) { const e = circ(0), u = (t - HERON.off) / 2.5; return { x: lerp(PERCH.x, e.x, u), y: lerp(PERCH.y, e.y, u) - Math.sin(Math.PI * u) * 6, flap: true, dir: 1 }; }
  if (t < 141) { const a = -TAU * 2 * (t - 119) / 22, p = circ(a); return { ...p, flap: hash(3, step(t, 120)) < 0.5, dir: Math.sin(a) > 0 ? 1 : -1 }; }
  if (t < HERON.gone) { const e = circ(0), u = (t - 141) / 9; return { x: lerp(e.x, 520, u), y: lerp(e.y, 6, u), flap: false, dir: 1 }; }
  if (t < HERON.back) return null;
  if (t < 214) { const u = (t - HERON.back) / 14; return { x: lerp(-20, 268, u), y: lerp(24, 44, u) - Math.sin(Math.PI * u) * 10, flap: u < 0.3, dir: 1 }; }
  const u = (t - 214) / 4; return { x: lerp(268, PERCH.x, u), y: lerp(44, PERCH.y, u), flap: true, dir: -1 };
}
function drawHeron(t) {
  const p = heronAt(t);
  if (!p) return;
  const x = Math.round(p.x), y = Math.round(p.y);
  if (p.perch) {                                       // hunched on the roof corner, a white cheek catching the light
    rect(ctx, x - 1, y - 4, 3, 4, '#5a6278'); rect(ctx, x - 1, y - 5, 2, 1, '#1a1c28'); rect(ctx, x + 1, y - 4, 1, 1, '#d8dce8'); rect(ctx, x + 2, y - 3, 1, 1, '#2a2a30');
    rect(ctx, x, y, 1, 1, '#c8a050');
    return;
  }
  const up = p.flap && (Math.floor(t * 5) & 1);
  ctx.fillStyle = '#7a8298';
  ctx.fillRect(x - 1, y, 4, 2); ctx.fillStyle = '#1a1c28'; ctx.fillRect(p.dir > 0 ? x + 2 : x - 1, y - 1, 1, 1);
  ctx.fillStyle = '#9aa2b8';
  if (up) { ctx.fillRect(x - 3, y - 2, 3, 1); ctx.fillRect(x + 2, y - 2, 3, 1); ctx.fillRect(x - 1, y - 1, 1, 1); ctx.fillRect(x + 2, y - 1, 1, 1); }
  else { ctx.fillRect(x - 4, y + 1, 4, 1); ctx.fillRect(x + 3, y + 1, 4, 1); }
}

/* ---- the upstairs shoji of the tea house: a shadow crossing it every 24 s, someone kneeling at the low table ---- */
function drawShoji(t) {
  ctx.drawImage(SHOJI_C, SHOJI_WIN.x0, SHOJI_WIN.y0);
  const c = t % SHOJI, sx = SHOJI_WIN.x0 - 6 + Math.round((c - 3) / 7 * (SHOJI_WIN.x1 - SHOJI_WIN.x0 + 12));
  ctx.globalAlpha = 0.55; ctx.fillStyle = '#4a2a1c';
  ctx.fillRect(SHOJI_WIN.x1 - 14, SHOJI_WIN.y0 + 11, 4, 4); ctx.fillRect(SHOJI_WIN.x1 - 16, SHOJI_WIN.y0 + 15, 9, 11);   // kneeling, still
  if (c >= 3 && c < 10) {                              // someone walks past the lamp: a long soft shadow
    ctx.fillRect(sx, SHOJI_WIN.y0 + 3, 5, 5); ctx.fillRect(sx - 2, SHOJI_WIN.y0 + 8, 9, 18);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(SHOJI_GRID, SHOJI_WIN.x0, SHOJI_WIN.y0);
}
function renderShoji(grid) {
  const w = SHOJI_WIN.x1 - SHOJI_WIN.x0 + 1, h = SHOJI_WIN.y1 - SHOJI_WIN.y0 + 1, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const bar = x % 8 === 0 || y % 6 === 0 || x === w - 1 || y === h - 1;
    if (grid) { if (bar) p.set(x, y, '#3a2416'); }
    else p.set(x, y, pick(['#c8904a', '#e8b868', '#f8dc98'], 0.85 - Math.hypot((x - w * 0.7) / w, (y - h * 0.6) / h) * 0.9, x, y));
  }
  return p.done();
}

/* ================= The picked weather ================= */
// Kyoto's own falling kind is maple leaves (a 'leaves' pick keeps them). Any other pick hides them and falls in their
// place, landing on the lane, the landing and out of the frame, catching the lanterns, the pagoda and the sign's lamps.
const WEATHER = weatherPlans(['leaves']);
const WX_LAND = [[150, 206], [208, 238], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: PAG.cx, y: 96, rx: 26, ry: 52, col: '#ffd090' }, { x: 36, y: 172, rx: 20, ry: 22, col: '#ff9a60' }, { x: 74, y: 172, rx: 20, ry: 22, col: '#ff9a60' },
  { x: TORO.x, y: 184, rx: 18, ry: 18, col: '#ffd080' }, { x: 172, y: 168, rx: 16, ry: 18, col: '#ffb070' }, { x: 308, y: 168, rx: 16, ry: 18, col: '#ffb070' },
  { x: 20, y: 176, rx: 18, ry: 14, col: '#ffcf80' }, { x: ANDON.x, y: ANDON.top + 6, rx: 16, ry: 16, col: '#ffe0a0' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS }, WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 25, y: BB.y + 8, rx: 30, ry: 16, col: '#e8f0c8' }] };

/* ================= Pre-render everything static ================= */
let HL, POOL, SKY_C, MOON_C, CLOUDS, HILLS, MIST_C, PAG_C, PAG_SHADE, LANE, BBF, BBAD, PITCH, TEA_C, PARASOL_C, WALL_C, TREE_C,
  LAND_C, DAPPLE_C, LEAF_SPR, SHORO_C, BELL_C, ANDON_C, SHOJI_C, SHOJI_GRID, CAT_SPR, CHO_R, CHO_W, CHO_BIG, VIG_C;
function renderVignette() {
  const p = pix(W, H), c = [4, 4, 16];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  HL = {
    moon: makeHalo(28, '#b8c4ff', 0.22, 1.8), pagoda: makeHalo(64, '#ffb060', 0.13, 1.5), tip: makeHalo(8, '#ffe0a0', 0.3),
    lantern: makeHalo(14, '#ff9a50', 0.4), lanternM: makeHalo(9, '#ff9a50', 0.35), lanternS: makeHalo(5, '#ff9a50', 0.3),
    tea: makeHalo(24, '#ff8a4a', 0.32, 1.6), toro: makeHalo(18, '#ffc870', 0.4), bbLamp: makeHalo(6, '#ffe8c8', 0.5), bbGlow: makeHalo(30, '#a0e0a0', 0.07),
    phone: makeHalo(5, '#bfe4ff', 0.5), counter: makeHalo(28, '#ffb060', 0.14, 1.5),
    bell: makeHalo(16, '#ffc070', 0.22), andon: makeHalo(20, '#ffd890', 0.36),
  };
  POOL = {
    laneL: makePool(18, 4, '#ff9a50', 0.22), laneM: makePool(11, 3, '#ff9a50', 0.2), laneS: makePool(6, 2, '#ff9a50', 0.18),
    tea: makePool(34, 7, '#ff9a50', 0.26), toro: makePool(26, 5, '#ffc870', 0.24),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer); HILLS = renderHills(); MIST_C = renderMist();
  PAG_C = renderPagoda(); PAG_SHADE = renderPagodaShade(PAG_C);
  CHO_R = []; CHO_W = [];
  for (let h = 2; h <= 10; h++) { CHO_R[h] = renderChochin(h, true); CHO_W[h] = renderChochin(h, false); }
  CHO_BIG = renderChochin(14, true);
  LANE = renderLane(); BBF = renderBBFrame(); BBAD = renderBBAd(); PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  TEA_C = renderTea(); PARASOL_C = renderParasol(); WALL_C = renderWall();
  TREE_C = Object.fromEntries(TREES.map((T, k) => [T.id, renderTree(T, 300 + k * 17)]));
  LAND_C = renderLanding(); DAPPLE_C = renderDapple(); SHORO_C = renderShoro(); BELL_C = renderBell(); ANDON_C = renderAndon();
  LEAF_SPR = LEAF_COLS.map(lc => [0, 1, 2].map(s => [makeLeaf(lc, s, false), makeLeaf(lc, s, true)]));
  SHOJI_C = renderShoji(false); SHOJI_GRID = renderShoji(true);
  CAT_SPR = { sit: [catSprite(CAT_SIT, false), catSprite(CAT_SIT, true)], walk: CAT_WALK_F.map(rows => [catSprite(rows, false), catSprite(rows, true)]) };
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
const PUDDLE_CLIP = [];
for (const q of PUDDLES) for (let y = q.y - q.ry; y <= q.y + q.ry; y++) {
  const w = Math.round(q.rx * Math.sqrt(Math.max(0, 1 - ((y - q.y) / (q.ry + 0.5)) ** 2)));
  if (w > 0) PUDDLE_CLIP.push([q.x - w, y, w * 2, 1]);
}
const STREAKS = [
  { x: 36, w: 6, col: '#ff9a50', a: 0.34, seed: 1 }, { x: 74, w: 6, col: '#ff9a50', a: 0.34, seed: 2 }, { x: TORO.x, w: 5, col: '#ffcf80', a: 0.3, seed: 3 },
  { x: 24, w: 8, col: '#ffcf80', a: 0.2, seed: 4 }, { x: 55, w: 9, col: '#ffb060', a: 0.22, seed: 5 }, { x: ANDON.x, w: 5, col: '#ffd890', a: 0.3, seed: 6 },
  { x: 308, w: 4, col: '#ff9a50', a: 0.2, seed: 7 }, { x: PAG.cx, w: 5, col: '#ffcf80', a: 0.16, seed: 8 },
];
const STREAKS_BB = [...STREAKS, { x: BB.x + 25, w: 6, col: '#c8e8a0', a: 0.14, seed: 9 }];
/** render(t) paints the city's own billboard ad; { pitch } paints one of the site's faces on it; { billboard: false }
 *  leaves the sign out (the loop videos); { weather } draws a WEATHERS pick, the city's own when left out;
 *  { crowd } is the share of the crowd to show, 0..1 (CROWD when left out or not a number); exactly 0 draws nobody */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  const crowd = Number.isFinite(share) ? clamp(share, 0, 1) : CROWD;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, g = gust(t);
  // sky, moon, clouds, the hills and their mist
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e6eaff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); } }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9); ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  blit(HILLS.c);
  for (const w of HILLS.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < 0.6) rect(ctx, w.x, w.y, 1, 1, '#ffc878');
  ctx.globalAlpha = 0.45; drawScroll(MIST_C, 112, 3, t); ctx.globalAlpha = 1;
  if (wx.leaves) for (const l of LEAVES[0]) drawLeaf(l, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the pagoda, its heron; the maples behind the tea house and the wall
  drawPagoda(t);
  drawHeron(t);
  drawShoro(t, crowd);
  drawTree(TREE_C.L, t, g); drawTree(TREE_C.R, t, g);
  // the lane: its houses and steps, lit windows, lantern light on the stones, its two small maples, the lanterns, the sign
  blit(LANE.c);
  drawLaneWins(t);
  drawLanePools(t);
  drawTree(TREE_C.l1, t, g * 0.5); drawTree(TREE_C.r1, t, g * 0.5);
  drawLaneLanterns(t, g);
  if (billboard) drawBillboard(t, pitch);
  // everyone, nearest last: those in the lane first (behind the corner buildings), the landing's after
  const DL = [];
  const put = (fy, rank, fn) => { if (rank < crowd) DL.push([fy, fn]); };
  const route = (r, tt, L, id, rank, fps, extra) => { const p = routeAt(r, tt); if (p) put(p.fy, rank, () => drawRouteWalker(r, L, tt, id, fps, extra)); };
  route(GEIKO_RT, t, LK.geiko, 1, 0, 2.4, geikoLantern);
  COUPLE_RT.forEach((r, i) => route(r, t, i ? LK.him : LK.her, 2 + i, 0, 3, i ? null : (p, ty, f) => couplePhoto(p, ty, f, t)));
  MAIKO_RT.forEach((r, i) => route(r, t, i ? LK.maikoB : LK.maikoA, 4 + i, 0, 2.6));
  for (const w of LANE_RT) route(w, t % w.P, w.L, w.id, w.rank, 3.4);
  for (const w of WK) {
    const u = frac(w.k * t / LOOP + w.ph), x = Math.round(w.dir > 0 ? -14 + u * 508 : 494 - u * 508);
    put(w.fy, w.rank, () => fig(x, w.fy, w.h, w.L, 'side', w.dir, walkFrame(t, w.fps, w.id)));
  }
  put(Math.round(gY(1 / 1.16)), 0, () => drawSweeper(t, g, crowd));
  put(210, 0, () => drawServer(t, crowd));
  put(252, 0, () => drawRickshaw(t, crowd));
  DL.sort((a, b) => a[0] - b[0]);
  let k = 0;
  for (; k < DL.length && DL[k][0] < 207; k++) DL[k][1]();
  if (wx.leaves) for (const l of LEAVES[1]) drawLeaf(l, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the corner tea house, its parasol and bench; the temple wall, its stone lantern and the cat
  blit(TEA_C);
  drawShoji(t);
  drawNoren(t, Math.max(g, norenPush(t)));
  halo(HL.counter, 20, 178, 0.8);
  drawCounter(t, crowd);
  for (const [i, L] of TEA_LANTERNS.entries()) {
    const sw = Math.round(g * 1.5 * wave(t, 52, i * 0.4));
    rect(ctx, L.x, L.y - 1, 1, 2, '#1a120e'); ctx.drawImage(CHO_BIG, L.x - 5 + sw, L.y + 1);
    halo(HL.tea, L.x + sw, L.y + 8, 0.8 + 0.1 * wave(t, 260 + i * 30, i * 0.5) + 0.06 * (hash(i, step(t, 2400), 7) - 0.5));
  }
  blit(PARASOL_C);
  drawBenchGuests(t, crowd);
  blit(WALL_C);
  drawDapple(t, g);
  halo(HL.toro, TORO.x, 184, 0.85 + 0.15 * wave(t, 330, 0.2) + 0.08 * (hash(9, step(t, 2400), 7) - 0.5));
  drawCat(t, g);
  blit(ANDON_C); halo(HL.andon, ANDON.x, ANDON.top + 6, 0.85 + 0.1 * wave(t, 280, 0.6));
  // the wet landing: the lit fronts mirrored in the stones and, sharper, in the puddles; lantern streaks; leaves in the wind
  ctx.drawImage(LAND_C, 0, LAND);
  reflect(ctx, t, { top: LAND, rows: H - LAND, squash: 1.25, alpha: 0.2, amp: 0.4, k1: 30, k2: 70 });
  ctx.save(); ctx.beginPath(); for (const r of PUDDLE_CLIP) ctx.rect(r[0], r[1], r[2], r[3]); ctx.clip();
  reflect(ctx, t, { top: LAND, rows: H - LAND, squash: 1.25, alpha: 0.6, amp: 0.5, k1: 40, k2: 96, grow: 0.02 });
  ctx.restore();
  for (const s of billboard ? STREAKS_BB : STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, LAND + 2, H);
  drawPool(POOL.tea, 55, LAND + 6, 0.8); drawPool(POOL.toro, TORO.x, LAND + 4, 0.8 + 0.1 * wave(t, 330, 0.2));
  drawGroundLeaves(t, g);
  for (; k < DL.length; k++) DL[k][1]();
  if (wx.leaves) for (const l of LEAVES[2]) drawLeaf(l, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'kyoto',
  name: 'Kyoto',
  tagline: 'Maple leaves on Ninenzaka',
  timeZone: 'Asia/Tokyo',
  accent: '#e4572e',                                   // = ACCENT, written out: tools/seo/seo.mjs reads it from the source
  focusX: PAG.cx,
  landmark: [PAG.cx - ROOFS[4].r - 2, PAG.cx + ROOFS[4].r + 2],   // the Yasaka Pagoda, eave tip to eave tip
  landmarkTop: PAG.tip,                                // the jewel on top of the sorin
  billboard: BB,
  crowd: CROWD,                                        // the share of the crowd shown by default; render(t, { crowd }) takes any 0..1
  ambience: { wind: 0.3, crickets: 0.15, crowd: 0.12 },
  events: BELLS.map(b => ({ t: b, type: 'bell', x: PAG.cx, gain: 0.6 })),
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
